import { z } from 'zod';
import { uuidSchema } from '../common';
import { AGENT_ROLES, type AgentRole } from '../../constants';
import { SUMMARIZATION_THRESHOLD_MIN, SUMMARIZATION_THRESHOLD_MAX } from '../../summarizer';
import { PREVIOUS_CONVERSATIONS_MAX, clampPreviousConversationsCount } from '../../scheduling/placeholders';
import { FLAG_CONDITION_OPERATORS, type FlagCondition } from '../../flag-conditions';
import { checkDecisionsModelBinding, readAgentSettingsModel } from '../../decisions-model';
import { NATIVE_TOOLS } from '../../catalog/native-tools';
import {
  catalogToolSideEffects,
  httpToolSideEffects,
  withComposedChain,
  type ToolSideEffects,
} from '../../catalog/tool-side-effects';
import { CONFIDENCE_VARIABLE_SUFFIX } from '../../decision-questions';
import type { StoredCallOpenings } from '../call-openings';

/**
 * Every native tool name the platform ships, for the one question this file asks of
 * it: is a name a rule wrote a NATIVE tool (whose admissibility the allowlist
 * decides) or something the hub configured (whose admissibility is a row question)?
 * A Set because the check runs per rule on every agent write.
 */
const NATIVE_TOOL_NAMES: ReadonlySet<string> = new Set(NATIVE_TOOLS.map((t) => t.tool_name));

// Re-exported beside the field it bounds, so a consumer of the contract gets the limit
// and the clamp with it rather than restating either. Their own home
// (`scheduling/placeholders`) has no export subpath, and the root `@wayai/core` barrel
// re-exports `./observability`, which needs `node:async_hooks` — Turbopack rejects that
// in a client bundle (see the note above the core imports in AgentDetailView.tsx).
export { PREVIOUS_CONVERSATIONS_MAX, clampPreviousConversationsCount };

// Same reason: the flag-condition operator enum and row type live in
// `../../flag-conditions` beside the evaluator that honours them, and the web picker
// reaches them through this contract barrel rather than the root one. The evaluator
// itself is NOT re-exported here — its consumers (backend turn path, the rule
// interpreter) import the `flag-conditions` module directly (`@wayai/core/flag-conditions`
// from outside the package).
export { FLAG_CONDITION_OPERATORS };
export type { FlagCondition, FlagConditionOperator } from '../../flag-conditions';

// Relocated from the hub (`updateHubBody`) to the summarizer agent — preserve the
// original int+range guard on the write paths that set it. `.passthrough()` keeps
// other agent keys flowing; this explicit key restores validation for this one.
// How many of the user's prior ended conversations this agent receives as context.
// A real `agent` column, so `.passthrough()` plus HubDO's column-derived writer would
// otherwise persist ANY value here — `"garbage"` included, which the turn path reads
// back as NaN and silently treats as off. Validated at the contract, where a bad
// write is a 400 the caller can see. 0 and null both mean off.
const previousConversationsCountField = z
  .number()
  .int()
  .min(0)
  .max(PREVIOUS_CONVERSATIONS_MAX)
  .nullable()
  .optional();

const summarizationThresholdField = z
  .number()
  .int()
  .min(SUMMARIZATION_THRESHOLD_MIN)
  .max(SUMMARIZATION_THRESHOLD_MAX)
  .nullable()
  .optional();

/**
 * One flag-condition row, gated by the shared operator enum. The evaluator honours
 * exactly the enum's members, so an operator this schema let through but the
 * evaluator did not recognise would persist a condition that never fires and says
 * nothing about it.
 */
const flagConditionSchema = z.object({
  variable: z.string(),
  operator: z.enum(FLAG_CONDITION_OPERATORS),
  // `boolean` is admitted for parity with the CI write path, whose refine gates the
  // operator alone, so `value: false` on a boolean evaluation variable is storable
  // config today and the evaluator honours it through `String()`. Narrowing here would
  // reject on PATCH exactly what `wayai push` persists, so any REST caller that reads
  // an agent and writes it back — the typed clients, the CLI's `ApiClient`, MCP — would
  // fail on a hub configured through the other surface.
  value: z.union([z.string(), z.number(), z.boolean()]),
});

// =============================================================================
// MONITOR DECISION CONTRACT — `trigger` is attached; the rule schemas are not
// =============================================================================

/**
 * A key in the agent contract gates a REST write. `monitor_config` is a
 * `.passthrough()` JSON column, so gating a key no reader honours yet would
 * validate the value and then answer 200 on a monitor that goes on behaving as
 * before — the "reads as configured, silently never fires" shape this file's
 * `flagConditionSchema` comment describes. So each key joins `monitorConfigField`
 * in the PR that ships its READER, and when it does it joins every write path at
 * once: REST here, and the CI push paths, where `contracts/ci.ts` carries the
 * matching refine because agents reach that column with no object schema at all.
 *
 * WHERE EACH KEY STANDS:
 * - `trigger` IS attached. Its reader is the idle scheduler, which arms only an
 *   `idle` monitor, so setting any other trigger stops that monitor being scheduled.
 * - `history_messages` and `include_tool_results` ARE attached. Their reader is the
 *   monitor side-call, which sizes its window and strips tool-result bodies from it.
 *   `include_tool_results` has a second effect: under a reply gate, the gate and a
 *   callee it runs that set it are also shown the turn's own tool results beside the
 *   draft, whatever `keep_in_history` and the window say (`MonitorDraftReply` in the
 *   backend's monitor side-call).
 *   Because only a side-call reads them, an `idle` monitor carrying either is
 *   REFUSED rather than silently ignored — see `MONITOR_INPUT_SHAPING_KEYS`.
 * - `rules` and `fallback` ARE attached. Their readers are the synchronous
 *   side-calls — `user_message`, the `assistant_reply` reply gate, and a `manual`
 *   callee's — which interpret them and carry the action out. `idle` has no runner
 *   and refuses them — see `MONITOR_RULE_TRIGGERS`. `rules[].when` is a third place a `FlagCondition`
 *   persists, so `refineHubAsCodeFlagConditions` visits it too.
 * - `speaker` IS attached. Its reader is call control, which runs a `call_utterance` monitor
 *   once per {@link CALL_UTTERANCE_CHECK_CHARS} new characters of the speech it names, so any
 *   other trigger carrying it is refused — see `MONITOR_CALL_KEYS`.
 *
 * `interpretMonitorDecision` in `monitor-decisions.ts` reads these shapes and
 * infers its types from them, so the interpreter and the write path cannot
 * describe different config.
 */

/**
 * When a monitor runs. `idle` is the delayed, debounced fire `delay_seconds`
 * describes; `user_message` and `assistant_reply` name the two synchronous points
 * inside a turn; `manual` names a monitor nothing schedules; `call_utterance` names a
 * live voice call's transcript as it grows (see {@link CALL_UTTERANCE_CHECK_CHARS});
 * `automation_fire` names an automation's fire gate — run by the hub before a fire,
 * when no conversation exists yet (see {@link MONITOR_AUTOMATION_FIRE_ACTION_KINDS});
 * `automation_item` names an automation's item gate — run for each contact of a fire's
 * list before anything is started for it (see {@link MONITOR_AUTOMATION_ITEM_ACTION_KINDS}).
 *
 * An absent `trigger` means `idle`, so the enum carries no separate "unset" member.
 */
export const MONITOR_TRIGGERS = [
  'idle', 'user_message', 'assistant_reply', 'manual', 'call_utterance', 'automation_fire', 'automation_item',
] as const;

export type MonitorTrigger = (typeof MONITOR_TRIGGERS)[number];

export const monitorTriggerSchema = z.enum(MONITOR_TRIGGERS);

/**
 * The trigger a stored `monitor_config` means, with the absent case resolved.
 *
 * Every reader goes through this rather than testing the raw value. "Absent means
 * `idle`" is what keeps a monitor written before this field existed running
 * exactly as it did, and a reader spelling that rule itself would be a second
 * copy of it — the shape where one site forgets it and an existing hub's monitor
 * stops being scheduled with nothing to show why.
 *
 * The parameter is `unknown` on purpose: `monitor_config` is a `.passthrough()`
 * JSON column, so a value reaching a reader need not have passed this file's
 * schema. An absent, null, or unrecognised `trigger` resolves to `idle` — the
 * answer that preserves today's behaviour, rather than one that stops scheduling
 * a monitor the operator can still see enabled.
 */
export function resolveMonitorTrigger(monitorConfig: unknown): MonitorTrigger {
  if (!monitorConfig || typeof monitorConfig !== 'object') return 'idle';
  const raw = (monitorConfig as { trigger?: unknown }).trigger;
  return (MONITOR_TRIGGERS as readonly string[]).includes(raw as string)
    ? (raw as MonitorTrigger)
    : 'idle';
}

/**
 * The triggers that gate an automation (wayai#4931 D11) rather than act on a conversation: its
 * fire gate (`automation_fire`) and its item gate (`automation_item`). Each runs before any
 * conversation exists, so its rules select from a fixed, tool-free action set
 * ({@link monitorRuleActionKinds}) and it reads no conversation's messages.
 */
export const AUTOMATION_GATE_TRIGGERS = ['automation_fire', 'automation_item'] as const satisfies readonly MonitorTrigger[];

export type AutomationGateTrigger = (typeof AUTOMATION_GATE_TRIGGERS)[number];

/** Whether `trigger` gates an automation (`AUTOMATION_GATE_TRIGGERS`). */
export function isAutomationGateTrigger(trigger: MonitorTrigger): trigger is AutomationGateTrigger {
  return (AUTOMATION_GATE_TRIGGERS as readonly MonitorTrigger[]).includes(trigger);
}

/**
 * The triggers something FIRES on a conversation. `manual` is not one: nothing schedules
 * it and nothing runs it on its own, so any number of `manual` monitors can sit on a hub
 * without the question "which one runs" arising. Nor is either automation gate
 * (`automation_fire`, `automation_item`): an automation names its gate by `agent_name`
 * (wayai#4931 D11), so each automation selects its own and a hub may hold as many as it has
 * automations to gate.
 *
 * Derived from `MONITOR_TRIGGERS` through the one predicate rather than listed
 * again, so a trigger added later joins the one-enabled-monitor rule by default
 * instead of missing it until someone notices a second list.
 */
const NON_FIRING_TRIGGERS = ['manual', ...AUTOMATION_GATE_TRIGGERS] as const satisfies readonly MonitorTrigger[];

export type MonitorFiringTrigger = Exclude<MonitorTrigger, (typeof NON_FIRING_TRIGGERS)[number]>;

export function isFiringTrigger(trigger: MonitorTrigger): trigger is MonitorFiringTrigger {
  return !(NON_FIRING_TRIGGERS as readonly MonitorTrigger[]).includes(trigger);
}

export const MONITOR_FIRING_TRIGGERS: readonly MonitorFiringTrigger[] =
  MONITOR_TRIGGERS.filter(isFiringTrigger);

/**
 * The stored or incoming spelling of an enabled agent, asked exactly as the two
 * monitor readers ask it — `signalMonitor` and `selectMonitor` both spell
 * `enabled === true || enabled === 1` inline, because the column is
 * `enabled INTEGER NOT NULL DEFAULT 1` and reaches a caller as a number where the
 * row is read raw and as a boolean where `sqlRowToObj` decoded it.
 */
export function isEnabledAgentValue(value: unknown): boolean {
  return value === true || value === 1;
}

/**
 * The firing trigger an agent row CLAIMS on its hub — the input to the
 * one-enabled-monitor-per-trigger rule — or null when it claims none.
 *
 * This is the SELECTION predicate both monitor readers already run, asked as a
 * question instead of a filter: `signalMonitor` takes the first enabled `monitor`
 * whose config resolves to `idle`, and `selectMonitor` the first whose config
 * resolves to the synchronous trigger it was called for. A row this function names
 * a trigger for is a row one of them can pick; the rule exists so that at most one
 * row per trigger can.
 *
 * So a row with NO `monitor_config` claims `idle`, because `resolveMonitorTrigger`
 * reads an absent trigger that way and `signalMonitor`'s `find` therefore matches
 * it — it wins the idle slot and then arms nothing, which is the hub whose real
 * idle monitor never fires. The same goes for a config that is an array or names
 * an unrecognised trigger.
 *
 * Claims nothing: a row that is not a `monitor` (a non-monitor agent can carry a
 * `monitor_config`); a DISABLED row, because the rule is about which monitor RUNS
 * and a spare kept switched off while a replacement is tried must stay saveable;
 * and a config naming `manual`, which fires nowhere.
 *
 * `monitor_config` is expected already decoded — an object or null, not the stored
 * JSON TEXT.
 */
export function monitorFiringTriggerClaim(row: {
  agent_role?: unknown;
  enabled?: unknown;
  monitor_config?: unknown;
}): MonitorFiringTrigger | null {
  if (row.agent_role !== 'monitor') return null;
  if (!isEnabledAgentValue(row.enabled)) return null;
  const trigger = resolveMonitorTrigger(row.monitor_config);
  return isFiringTrigger(trigger) ? trigger : null;
}

/**
 * What an operator is told when a save or a push would put two enabled monitors on
 * one firing trigger. One literal for both surfaces — the REST agent routes answer
 * it as a 400, the CI push/diff path as a config rejection — because an operator
 * who meets the rule on one and then the other must not be told two different
 * things.
 */
export function monitorTriggerClashMessage(
  trigger: MonitorFiringTrigger,
  agentNames: string[],
): string {
  return `A hub runs at most one enabled monitor on the "${trigger}" trigger. `
    + `This would leave ${agentNames.length}: ${agentNames.map((name) => `"${name}"`).join(', ')}. `
    + 'Disable all but one, or move the rest to another trigger.';
}

/**
 * The floor on `monitor_config.delay_seconds` — the guard the hub UI enforced
 * before the field moved onto the monitor agent.
 *
 * Named because the write paths that apply it have to agree: the Zod field below
 * (REST), and the CI diff and apply paths, which carry no Zod for the agent
 * object and clamp instead.
 */
export const MONITOR_DELAY_SECONDS_MIN = 10;

/**
 * The message both write surfaces answer with when an `idle` monitor declares no
 * delay. One literal, because the REST field and the CI refine enforce the same
 * rule and an operator who meets it on one surface and then the other must not be
 * told two different things.
 */
export const MONITOR_IDLE_DELAY_REQUIRED_MESSAGE =
  'delay_seconds is required for an idle monitor (an absent trigger is idle)';

/**
 * Is this a `monitor_config` that names the `idle` fire but no delay to fire on?
 *
 * The rule both write surfaces ask: `delay_seconds` describes the `idle` fire and
 * nothing else reads it, so every other trigger may omit it and `idle` — a
 * declared one OR an absent trigger — may not.
 */
export function monitorConfigNeedsDelay(monitorConfig: unknown): boolean {
  if (!monitorConfig || typeof monitorConfig !== 'object') return false;
  return resolveMonitorTrigger(monitorConfig) === 'idle'
    && (monitorConfig as { delay_seconds?: unknown }).delay_seconds === undefined;
}

/**
 * Clamp a `monitor_config`'s DECLARED `delay_seconds` to the floor, for the CI
 * paths that are about to persist it.
 *
 * The CI surfaces carry NO Zod object schema for the agent — agents ride
 * `hubAsCodeConfigSchema`'s `.passthrough()` — so the floor the REST field
 * enforces is applied here instead. It is one function because the two CI sites
 * that call it have to agree: a diff computed under one rule and an apply written
 * under another re-diffs forever.
 *
 * ABSENT versus UNUSABLE are different, and the difference is the whole of this
 * function:
 *
 * - An ABSENT `delay_seconds` is left absent. Both call sites sit behind
 *   `refineHubAsCodeMonitorConfig`, which refuses an `idle` config carrying no
 *   delay, so an absent one can only belong to a trigger that has no use for it —
 *   and inventing one is the churn this function exists to stop: both CI sites
 *   used to write `delay_seconds` unconditionally, so a `manual` or `user_message`
 *   monitor declaring no delay took one on the first push and then differed from
 *   its own workspace YAML on every push after it.
 * - A PRESENT but unusable one — `'300s'`, `null`, a bare string — is floored. The
 *   refine tests only whether the key is there, and this path carries no object
 *   schema, so a non-numeric value reaches the column. Left verbatim it reads as an
 *   `idle` monitor to every trigger consumer while `signalMonitor` refuses to arm
 *   it (`typeof … !== 'number'`), which is a monitor that occupies the hub's idle
 *   trigger — blocking a replacement — and never runs.
 *
 * Unknown keys are preserved: the object is spread, never rebuilt.
 */
export function clampMonitorConfigDelay<T extends Record<string, unknown>>(monitorConfig: T): T {
  if (monitorConfig.delay_seconds === undefined) return { ...monitorConfig };
  const raw = Number(monitorConfig.delay_seconds);
  const usable = Number.isFinite(raw)
    ? Math.max(MONITOR_DELAY_SECONDS_MIN, Math.round(raw))
    : MONITOR_DELAY_SECONDS_MIN;
  return { ...monitorConfig, delay_seconds: usable };
}

/**
 * The stored shape of `monitor_config`, for the surfaces that carry a TYPE for
 * the column rather than a schema — `AgentResponse`, `HubAsCodeAgent`, the CI
 * diff engine's `DbAgent`, the CI pull cast, and the shared hub-config builder.
 *
 * One declaration because those five described the same object separately, and
 * adding `trigger` had to edit all five before a type error could be raised
 * anywhere. Deliberately a type alias and not an interface: the CI clamp takes
 * `T extends Record<string, unknown>`, which an interface is not assignable to.
 *
 * It is NOT the authority on what may be WRITTEN — `monitorConfigField` is, and
 * it is `.passthrough()`, so a stored value may carry keys this type does not
 * name (the rule keys above, until their readers ship).
 */
export type MonitorConfig = {
  delay_seconds?: number;
  trigger?: MonitorTrigger;
  /**
   * Side-call triggers only — how many of the newest messages the monitor reads. Tool
   * calls and results do not count; the ones among those messages come along.
   */
  history_messages?: number;
  /**
   * Side-call triggers only — also show what a tool that keeps its calls in history
   * returned (the window shows every call either way), and under a reply gate show the
   * turn's own tool results beside the draft.
   */
  include_tool_results?: boolean;
  flag_conditions?: FlagCondition[];
  /** Rule triggers only (`MONITOR_RULE_TRIGGERS`) — first match selects the action. */
  rules?: MonitorRule[];
  /** Rule triggers but `call_utterance` — the action when no rule matched. */
  fallback?: MonitorAction;
  /** `call_utterance` only — whose speech a check waits for (`resolveCallUtteranceSpeaker`). */
  speaker?: CallUtteranceSpeaker;
};

/** How many of the newest messages a monitor reads when `history_messages` is unset. */
export const MONITOR_HISTORY_MESSAGES_DEFAULT = 10;

/**
 * The ceiling on `history_messages`. The field sizes a model request, so a window
 * nothing could serve is refused where it is written rather than stored.
 */
export const MONITOR_HISTORY_MESSAGES_MAX = 100;

export const monitorHistoryMessagesSchema = z
  .number()
  .int()
  .min(1)
  .max(MONITOR_HISTORY_MESSAGES_MAX);

/**
 * Whether a monitor's window also shows what a tool that keeps its calls in history
 * returned, or only the calls — and, under a reply gate, whether the turn's own tool
 * results ride beside the draft.
 */
export const monitorIncludeToolResultsSchema = z.boolean();

/**
 * The keys that size and shape a SIDE-CALL's input, and mean nothing anywhere else.
 *
 * An `idle` monitor runs as a full turn through the ordinary history loader, which
 * neither key reaches. Rather than let them read as configured and do nothing there
 * — the failure this file's `flagConditionSchema` comment describes — an `idle`
 * monitor carrying one is refused. That is the mirror of `delay_seconds`, which is
 * required ONLY for `idle`.
 */
export const MONITOR_INPUT_SHAPING_KEYS = ['history_messages', 'include_tool_results'] as const;

/**
 * Each shaping key's value schema, as an optional field — ONE declaration that both
 * write surfaces use: `monitorConfigField` below spreads it, and
 * `refineHubAsCodeMonitorConfig` in `contracts/ci.ts` reads it by key.
 *
 * `satisfies Record<(typeof MONITOR_INPUT_SHAPING_KEYS)[number], …>` is what makes that
 * shared: adding a member to the constant without a schema here is a compile error, so
 * a third shaping key cannot be gated on one surface and forgotten on the other. The
 * REST field used to spell its keys out separately, which delivered that coupling for
 * the idle refusal only.
 */
export const MONITOR_INPUT_SHAPING_SCHEMAS = {
  history_messages: monitorHistoryMessagesSchema.optional(),
  include_tool_results: monitorIncludeToolResultsSchema.optional(),
} satisfies Record<(typeof MONITOR_INPUT_SHAPING_KEYS)[number], z.ZodTypeAny>;

/** Which input-shaping keys a config carries that its own trigger cannot read. */
export function monitorInputShapingKeysOnIdle(monitorConfig: unknown): string[] {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  if (resolveMonitorTrigger(monitorConfig) !== 'idle') return [];
  const config = monitorConfig as Record<string, unknown>;
  return MONITOR_INPUT_SHAPING_KEYS.filter((key) => config[key] !== undefined);
}

export const MONITOR_INPUT_SHAPING_IDLE_MESSAGE =
  'only a user_message, assistant_reply, manual or call_utterance monitor reads this — an idle monitor '
  + 'runs as a full turn and takes the ordinary history window';

/**
 * How many of the newest messages a side-call reads, from a config that may never
 * have passed this file's schema.
 *
 * Tolerant on purpose. `monitor_config` is a `.passthrough()` JSON column and both
 * input-shaping keys reached it UNVALIDATED before they were gated here, so a
 * hand-written value can already be stored. Validation belongs at the write edge; a
 * reader that threw on one would take a monitor down at run time over a value it was
 * handed rather than asked for.
 */
export function resolveMonitorHistoryMessages(monitorConfig: unknown): number {
  if (!monitorConfig || typeof monitorConfig !== 'object') return MONITOR_HISTORY_MESSAGES_DEFAULT;
  const raw = (monitorConfig as { history_messages?: unknown }).history_messages;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return MONITOR_HISTORY_MESSAGES_DEFAULT;
  return Math.min(MONITOR_HISTORY_MESSAGES_MAX, Math.max(1, Math.round(raw)));
}

/**
 * Whether a side-call's window shows what a tool that keeps its calls in history
 * returned, and — under a reply gate — the turn's own tool results beside the draft.
 * Anything but `true` does neither.
 */
export function resolveMonitorIncludeToolResults(monitorConfig: unknown): boolean {
  if (!monitorConfig || typeof monitorConfig !== 'object') return false;
  return (monitorConfig as { include_tool_results?: unknown }).include_tool_results === true;
}

/**
 * Where one tool argument comes from: a variable the monitor produced, or a
 * constant the admin wrote. Both members are `.strict()` so `{ from_varaible: … }`
 * is a 400 rather than an argument that silently resolves to nothing.
 *
 * There is no third member: an argument's value comes from config or from a
 * variable, never from anything the model itself gets to name.
 */
export const monitorArgumentSourceSchema = z.union([
  z.object({ from_variable: z.string().min(1) }).strict(),
  z.object({ const: z.union([z.string(), z.number(), z.boolean()]) }).strict(),
]);

/**
 * The longest note TEMPLATE an admin may save, in characters.
 *
 * ONE number for "how much text a monitor may put in front of a model", which is
 * why `monitorActionSchema`'s `rewrite.note` below is declared AGAINST this
 * constant instead of repeating the literal: the two are the same question asked on
 * two triggers, and a second literal is how they come to disagree. It is declared
 * above that schema for exactly that reason — a `const` a schema reads during module
 * initialisation has to precede it.
 *
 * A SAVE-TIME bound on the template, which is not the same bound as the rendered
 * note: substitution can only make the text longer, so the run time caps the
 * RENDERED string as well ({@link MONITOR_NOTE_RENDERED_MAX}). Both exist because
 * neither implies the other — a 10-character template with one variable holding a
 * paragraph passes this and needs the other.
 */
export const MONITOR_NOTE_TEMPLATE_MAX = 2000;

/**
 * The longest RENDERED note the runtime will deliver, in characters.
 *
 * Deliberately larger than the template cap, and the gap is the substitution
 * budget: a template at the ceiling with variables filled in is still delivered
 * rather than dropped for being a few characters over. A note past this is
 * TRUNCATED, not refused — the brief is still worth delivering, and refusing it
 * would let a long variable silently remove a briefing the admin configured.
 */
export const MONITOR_NOTE_RENDERED_MAX = 4000;

/**
 * The longest note a `steer` action may carry, in characters.
 *
 * A steer's note is appended to a live call's voice session VERBATIM — never rendered, never
 * cut — so this bounds exactly what reaches the session. Appends stay in the session for the
 * rest of the call ({@link CALL_MAX_STEERS_PER_CALL}), which is why it is short: a steer is a
 * course correction, not a script. It must also fit one provider append, which the backend
 * pins (`APPEND_MAX_CHARS` in `call-control/voice-provider.ts`).
 */
export const MONITOR_STEER_NOTE_MAX = 500;

/**
 * The longest agent name an automation gate may name — its monitor (`automation.gate.monitor`) or
 * a `run_with_agent` or `start_with_agent` action's agent. A bound these references set, not one `agent_name` has: it
 * keeps an automation row's size stateable (`AUTOMATIONS_PER_HUB_MAX`), so an agent named past it
 * cannot be named by a gate.
 */
export const MONITOR_GATE_AGENT_NAME_MAX = 255;

/**
 * What a matching rule resolves to.
 *
 * `call_tool` names a tool by string; whether the monitor may call it is decided
 * against the monitor's assigned tools, which a schema cannot know. `hold` and
 * `rewrite` act on a drafted reply rather than on a tool. `steer` acts on a live
 * voice call: its `note` — the hub's own words, never anything the monitor produced —
 * is appended to the call's voice session as an instruction (`call_utterance` only).
 */
export const monitorActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }).strict(),
  z
    .object({
      kind: z.literal('call_tool'),
      tool_name: z.string().min(1),
      args: z.record(monitorArgumentSourceSchema).optional(),
    })
    .strict(),
  z.object({ kind: z.literal('hold') }).strict(),
  z.object({ kind: z.literal('rewrite'), note: z.string().min(1).max(MONITOR_NOTE_TEMPLATE_MAX) }).strict(),
  z.object({ kind: z.literal('steer'), note: z.string().min(1).max(MONITOR_STEER_NOTE_MAX) }).strict(),
  // An automation's fire gate (`automation_fire` only): run the fire as configured, run it with
  // another of the hub's agents — named by `agent_name`, which survives publish and push the way
  // the gate's own name does — or skip it.
  z.object({ kind: z.literal('run') }).strict(),
  z.object({ kind: z.literal('run_with_agent'), agent: z.string().trim().min(1).max(MONITOR_GATE_AGENT_NAME_MAX) }).strict(),
  // An automation's item gate (`automation_item` only), for one contact of a fire's list: start
  // its work as configured, or start it with another of the hub's agents (by `agent_name`, as
  // `run_with_agent` names one). Both gates' `skip`, below, starts nothing.
  z.object({ kind: z.literal('start') }).strict(),
  z.object({ kind: z.literal('start_with_agent'), agent: z.string().trim().min(1).max(MONITOR_GATE_AGENT_NAME_MAX) }).strict(),
  z.object({ kind: z.literal('skip') }).strict(),
]);

/**
 * One rule: the conditions, then the action they select.
 *
 * `when` carries at least one condition because an empty array is a rule whose
 * meaning no reader can recover — the unconditional action is `fallback`.
 * `interpretMonitorDecision` treats an empty `when` as no match for the same
 * reason, so a rule that reaches the column through a path this schema does not
 * gate still cannot fire on nothing.
 */
export const monitorRuleSchema = z
  .object({
    when: z.array(flagConditionSchema).min(1),
    action: monitorActionSchema,
  })
  .strict();

export type MonitorAction = z.infer<typeof monitorActionSchema>;
export type MonitorRule = z.infer<typeof monitorRuleSchema>;

/**
 * The keys that say what a monitor DOES with its own variables.
 *
 * Their reader is the `user_message` side-call, which interprets them through
 * `interpretMonitorDecision` and carries the resolved action out through
 * `handleTool`. Shaped like `MONITOR_INPUT_SHAPING_KEYS` above and for the same
 * reason: one declaration both write surfaces read, so a third decision key
 * cannot be gated on REST and forgotten on the CI push.
 */
export const MONITOR_RULE_KEYS = ['rules', 'fallback'] as const;

export const MONITOR_RULE_SCHEMAS = {
  rules: z.array(monitorRuleSchema).optional(),
  fallback: monitorActionSchema.optional(),
} satisfies Record<(typeof MONITOR_RULE_KEYS)[number], z.ZodTypeAny>;

/**
 * The triggers whose runner interprets `rules` (and, on all but `call_utterance`, `fallback`).
 *
 * `user_message`, `assistant_reply`, `manual` and `call_utterance`. `idle` runs as a full turn through
 * `processMonitorStructuredOutput`, which calls no interpreter, so a key there would
 * read as configured and do nothing; it is refused where it is written — the same
 * reading `MONITOR_INPUT_SHAPING_KEYS` gets, and the mirror of `delay_seconds` being
 * required only for `idle`.
 *
 * `manual` JOINED THIS LIST WITH `run_monitor`, which is what gives it a runner. A
 * callee that could not act on its rules would be a monitor that judges and then
 * does nothing — the cascade exists precisely so the callee can act. It reaches the
 * same interpreter through the same side-call; only its selection differs (by name,
 * not by trigger).
 *
 * `assistant_reply` JOINED WITH THE REPLY GATE, which is its runner: the side-call
 * that judges the answering agent's DRAFT after its completion and before anything is
 * delivered. What its rules may select differs from the other two — see
 * {@link monitorRuleActionKinds} and {@link isMonitorRuleCallableTool}.
 *
 * `call_utterance` JOINED WITH ITS RUNNER, call steering (D8): a live call's transcript,
 * judged as it grows, whose rules may `steer` the voice with the hub's own note or call a
 * tool with no side effects, and act at most once per call each — see
 * {@link MONITOR_CALL_UTTERANCE_ACTION_KINDS}, {@link callUtteranceRuleIssues} and
 * {@link isMonitorRuleCallableTool}.
 *
 * A trigger joins this list in the PR that ships its runner.
 */
export const MONITOR_RULE_TRIGGERS: readonly MonitorTrigger[] = [
  'user_message', 'assistant_reply', 'manual', 'call_utterance', 'automation_fire', 'automation_item',
];

export const MONITOR_RULE_TRIGGER_MESSAGE =
  'only a user_message, assistant_reply, manual, call_utterance, automation_fire or automation_item monitor acts '
  + 'on rules — set one of those triggers, or remove this key. A manual monitor acts on its rules when another '
  + 'monitor runs it.';

/** Which decision keys a config carries that its own trigger cannot read. */
export function monitorRuleKeysOffTrigger(monitorConfig: unknown): string[] {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  if (MONITOR_RULE_TRIGGERS.includes(resolveMonitorTrigger(monitorConfig))) return [];
  const config = monitorConfig as Record<string, unknown>;
  return MONITOR_RULE_KEYS.filter((key) => config[key] !== undefined);
}

// =============================================================================
// CALL STEERING — the `call_utterance` trigger (D8, realtime voice calls, gh #4418)
// =============================================================================

/**
 * A `call_utterance` monitor judges a live voice call's transcript AS IT GROWS — both
 * speakers' words, newest last, before an utterance is final — and its rules may correct the
 * voice while the call runs. Call control decides WHEN a check runs (every
 * {@link CALL_UTTERANCE_CHECK_CHARS} new characters of the speech {@link resolveCallUtteranceSpeaker}
 * names, capped per call); the conversation runs it (`conversation-call-steering-ops.ts`).
 *
 * WHAT IT IS NOT: a gate. The voice's own speech reaches the caller before any check sees it,
 * so a steer cannot un-say anything — it changes what the voice says NEXT. Its limits are the
 * design record's (plan §6), and each is a rule here:
 * - a misheard transcript causes false positives, so every rule must be high-confidence
 *   ({@link CALL_STEERING_MIN_CONFIDENCE});
 * - an append is permanent for the session, so steers are capped per call
 *   ({@link CALL_MAX_STEERS_PER_CALL}) and each rule acts at most once a call;
 * - a steer's text is ALWAYS the rule's note, never text the monitor produced — otherwise a
 *   caller could talk the monitor into instructing the voice;
 * - nobody confirms what a rule does on a call, so it may call only a tool with no side
 *   effects ({@link isMonitorRuleCallableTool}).
 */

/** Whose speech a check waits for: the caller's, the voice's, or either side's. */
export const CALL_UTTERANCE_SPEAKERS = ['caller', 'voice', 'both'] as const;

export type CallUtteranceSpeaker = (typeof CALL_UTTERANCE_SPEAKERS)[number];

export const callUtteranceSpeakerSchema = z.enum(CALL_UTTERANCE_SPEAKERS);

/** What an absent `speaker` means: either side's speech counts toward the next check. */
export const CALL_UTTERANCE_SPEAKER_DEFAULT: CallUtteranceSpeaker = 'both';

/** Is this a speaker a `call_utterance` monitor may name? */
export function isCallUtteranceSpeaker(value: unknown): value is CallUtteranceSpeaker {
  return (CALL_UTTERANCE_SPEAKERS as readonly unknown[]).includes(value);
}

/**
 * The speaker a stored `monitor_config` means. Tolerant, like `resolveMonitorTrigger`: the
 * column is `.passthrough()` JSON, so an absent or unrecognised value reads as the default.
 */
export function resolveCallUtteranceSpeaker(monitorConfig: unknown): CallUtteranceSpeaker {
  if (!monitorConfig || typeof monitorConfig !== 'object') return CALL_UTTERANCE_SPEAKER_DEFAULT;
  const raw = (monitorConfig as { speaker?: unknown }).speaker;
  return isCallUtteranceSpeaker(raw) ? raw : CALL_UTTERANCE_SPEAKER_DEFAULT;
}

/**
 * The keys only a `call_utterance` monitor reads. Shaped like `MONITOR_INPUT_SHAPING_KEYS`, for
 * its reason: one declaration both write surfaces read, and any other trigger carrying one is
 * refused rather than left reading as configured.
 */
export const MONITOR_CALL_KEYS = ['speaker'] as const;

export const MONITOR_CALL_SCHEMAS = {
  speaker: callUtteranceSpeakerSchema.optional(),
} satisfies Record<(typeof MONITOR_CALL_KEYS)[number], z.ZodTypeAny>;

/** Which call keys a config carries that its own trigger cannot read. */
export function monitorCallKeysOffTrigger(monitorConfig: unknown): string[] {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  if (resolveMonitorTrigger(monitorConfig) === 'call_utterance') return [];
  const config = monitorConfig as Record<string, unknown>;
  return MONITOR_CALL_KEYS.filter((key) => config[key] !== undefined);
}

export const MONITOR_CALL_KEY_MESSAGE =
  'only a call_utterance monitor reads this: it names whose speech on a live call a check waits for';

/**
 * How many new characters of the speech a `call_utterance` monitor's `speaker` names start its
 * next check — the cadence OpenAI's Agents SDK uses on the Realtime API (the research note on
 * gh #4418). pt-BR speech runs at about 15 characters a second, so a monitor checks at most
 * about 10 times a call-minute. Call control coalesces: one check at a time per call, and one
 * more at once when this many characters arrived while it ran.
 */
export const CALL_UTTERANCE_CHECK_CHARS = 100;

/**
 * The most checks one call runs. OWNER-REVISABLE. At the cadence above it is about 30 minutes
 * of continuous speech; past it the call goes on unsteered. Call control counts each check
 * durably BEFORE sending it, so a restart cannot reset the count.
 */
export const CALL_UTTERANCE_MAX_RUNS_PER_CALL = 300;

/**
 * The most steers one call receives. OWNER-REVISABLE. An append stays in the voice's session
 * for the rest of the call, and shares the provider's per-session append budget with the call's
 * cues and replies, so a call is corrected a few times at most. The conversation counts them
 * off its own audit rows, in the same synchronous step that writes the row a steer leaves.
 */
export const CALL_MAX_STEERS_PER_CALL = 3;

/**
 * THE PLATFORM MINIMUM of the high-confidence rule. OWNER-REVISABLE.
 *
 * Every `call_utterance` rule must condition on one of its monitor's declared numeric
 * `*_confidence` variables (the platform's confidence convention, `CONFIDENCE_VARIABLE_SUFFIX`)
 * at `>=` this value or above, on the 0–1 scale a decisions model reports. A write without one
 * is refused, and at run time a stored rule without one steps aside before any rule is chosen.
 *
 * A STRUCTURAL GUARD, NOT PROOF. It proves a rule was written against a confidence, not that
 * the confidence is right: a monitor that heard a misheard transcript is confidently wrong, and
 * a generating model's confidence is self-reported. Whether steering interrupts wrongly is
 * measured on real calls before any customer hub gets them (G1).
 */
export const CALL_STEERING_MIN_CONFIDENCE = 0.9;

/** One condition that makes a `call_utterance` rule high-confidence. */
export interface CallUtteranceConfidenceCondition {
  variable: string;
  operator: '>=' | '>';
  /** A number, or its text — the Settings editor holds every condition value as text. */
  value: number | string;
}

/**
 * The conditions of one rule that meet the high-confidence rule, structurally: a
 * `*_confidence` variable, compared `>=` against a number from {@link CALL_STEERING_MIN_CONFIDENCE}
 * to 1, or `>` against one below 1. A bar above 1 is refused rather than admitted: on the 0–1
 * scale it could never match, and a rule that reads as configured and never fires is the failure
 * every refusal in this file exists to stop. The bar may be the number's TEXT, as the Settings
 * editor writes every condition value: the evaluator compares a numeric string numerically, so
 * `'0.9'` means what `0.9` means.
 *
 * THE ONE READING the write paths and the runtime share, over the raw rule — the column is
 * `.passthrough()`, so a malformed rule has none.
 */
export function callUtteranceConfidenceConditions(rule: unknown): CallUtteranceConfidenceCondition[] {
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return [];
  const when = (rule as { when?: unknown }).when;
  if (!Array.isArray(when)) return [];
  return when.filter(isConfidenceCondition);
}

function isConfidenceCondition(condition: unknown): condition is CallUtteranceConfidenceCondition {
  if (!condition || typeof condition !== 'object' || Array.isArray(condition)) return false;
  const { variable, operator, value } = condition as Record<string, unknown>;
  if (typeof variable !== 'string' || variable.length <= CONFIDENCE_VARIABLE_SUFFIX.length) return false;
  if (!variable.endsWith(CONFIDENCE_VARIABLE_SUFFIX)) return false;
  const bar = numericBar(value);
  if (bar === null || bar < CALL_STEERING_MIN_CONFIDENCE) return false;
  if (operator === '>=') return bar <= 1;
  if (operator === '>') return bar < 1;
  return false;
}

/** A condition value as the evaluator orders it: a finite number, or non-blank numeric text. */
function numericBar(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The evaluation-variable types a confidence may be declared as: the numeric ones. */
export const NUMERIC_EVALUATION_VARIABLE_TYPES = ['number', 'integer'] as const satisfies readonly (typeof AGENT_PARAMETER_TYPES)[number][];

export function isNumericEvaluationVariableType(type: unknown): boolean {
  return (NUMERIC_EVALUATION_VARIABLE_TYPES as readonly unknown[]).includes(type);
}

/**
 * Can a `call_utterance` rule be high-confidence on this run's own answer? One of its
 * confidence conditions must read a variable the run produced as a DECLARED number —
 * `numericVariables` is the names extraction typed numeric, which only a numeric declaration
 * yields. The run-time half of the write rule: a stored rule can reach the column by a path
 * the write gate does not cover, and a declaration can change after the rule was saved.
 *
 * Asked of every rule BEFORE any is chosen, never of the matched one after: a rule that fails it
 * steps aside, so it cannot win the first match and hide a later rule that could act
 * (`decide` in the backend's `conversation-monitor-action.ts`).
 */
export function callUtteranceRuleConfident(rule: unknown, numericVariables: ReadonlySet<string>): boolean {
  return callUtteranceConfidenceConditions(rule).some((condition) => numericVariables.has(condition.variable));
}

export const CALL_UTTERANCE_RULE_NOT_CONFIDENT_MESSAGE =
  'a call_utterance rule acts on a live call from a transcript that can be misheard, so it must be '
  + `high-confidence: give it a condition on one of this monitor's "…${CONFIDENCE_VARIABLE_SUFFIX}" `
  + `variables with ">= ${CALL_STEERING_MIN_CONFIDENCE}" or a higher bar, up to 1.`;

export const CALL_UTTERANCE_FALLBACK_MESSAGE =
  'a call_utterance monitor acts only when a high-confidence rule matches — a fallback would act on '
  + 'every check of the call. Remove it.';

export const CALL_UTTERANCE_FLAG_CONDITIONS_MESSAGE =
  'a call_utterance monitor checks a live call every few seconds and does not flag the conversation. '
  + 'Remove its flag_conditions, or flag from a monitor on another trigger.';

export const CALL_STEER_NOTE_BLANK_MESSAGE =
  'a steer\'s note is what the voice is told — write the instruction it should follow.';

export function callUtteranceConfidenceNotDeclaredMessage(variable: string): string {
  return `"${variable}" is not one of this monitor's evaluation variables of type number — a `
    + 'call_utterance rule\'s confidence must be a number the monitor itself declares. Declare it, '
    + 'then add the rule.';
}

/**
 * Every refusal only a `call_utterance` monitor meets that is decidable from the config alone:
 * a `fallback` (it would act on every check), `flag_conditions` (a check every few seconds is not
 * a flagging point), a rule without a high-confidence condition, and a `steer` whose note is
 * blank. Reported by {@link collectMonitorRuleIssues}, so both write surfaces and the Settings
 * editor meet them through the function they already call.
 *
 * Whether the confidence variable is DECLARED numeric is a hub question —
 * {@link checkCallUtteranceConfidenceDeclared}.
 */
export function callUtteranceRuleIssues(monitorConfig: unknown): MonitorRuleIssue[] {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  if (resolveMonitorTrigger(monitorConfig) !== 'call_utterance') return [];
  const config = monitorConfig as { rules?: unknown; fallback?: unknown; flag_conditions?: unknown };
  const issues: MonitorRuleIssue[] = [];
  if (config.fallback !== undefined) {
    issues.push({ path: ['fallback'], message: CALL_UTTERANCE_FALLBACK_MESSAGE });
  }
  if (Array.isArray(config.flag_conditions) && config.flag_conditions.length > 0) {
    issues.push({ path: ['flag_conditions'], message: CALL_UTTERANCE_FLAG_CONDITIONS_MESSAGE });
  }
  if (Array.isArray(config.rules)) {
    config.rules.forEach((rule, index) => {
      if (callUtteranceConfidenceConditions(rule).length === 0) {
        issues.push({ path: ['rules', index, 'when'], message: CALL_UTTERANCE_RULE_NOT_CONFIDENT_MESSAGE });
      }
      const action = rule && typeof rule === 'object' ? (rule as { action?: unknown }).action : undefined;
      const { kind, note } = (action && typeof action === 'object' ? action : {}) as { kind?: unknown; note?: unknown };
      if (kind === 'steer' && typeof note === 'string' && note.trim() === '') {
        issues.push({ path: ['rules', index, 'action', 'note'], message: CALL_STEER_NOTE_BLANK_MESSAGE });
      }
    });
  }
  return issues;
}

/**
 * Whether an agent write is one the voice-calls rollout gate judges: it leaves an ENABLED
 * `call_utterance` monitor where the stored row was not one — a create, a move onto the
 * trigger, a role change to `monitor`, or turning one on. The dark rule of the program
 * (every phase-1 surface reads P1-02's gate): a hub whose org has no calls cannot start steering
 * any. The carve-outs are `voiceAgentWriteNeedsGate`'s: turning one off, editing one without
 * turning it on, and deleting one stay allowed while the gate is closed.
 *
 * Judged on the EFFECTIVE row — each field the write carries over the stored one, a create
 * (`stored` null) being on unless it says otherwise (`enabled INTEGER NOT NULL DEFAULT 1`) —
 * with `monitor_config` decoded here, whether an object or the column's JSON text, so HubDO and
 * the CI precheck cannot read a partial write differently. `enabled` arrives as its writer
 * stores it; the claim is `monitorFiringTriggerClaim`, the one-monitor-per-trigger rule's own.
 */
export function callSteeringWriteNeedsGate(
  stored: { agent_role?: unknown; enabled?: unknown; monitor_config?: unknown } | null,
  write: { agent_role?: unknown; enabled?: unknown; monitor_config?: unknown },
): boolean {
  const field = (key: 'agent_role' | 'monitor_config'): unknown => (write[key] !== undefined ? write[key] : stored?.[key]);
  const effective = {
    agent_role: field('agent_role'),
    enabled: write.enabled !== undefined ? write.enabled : (stored ? stored.enabled : true),
    monitor_config: decodedMonitorConfig(field('monitor_config')),
  };
  if (monitorFiringTriggerClaim(effective) !== 'call_utterance') return false;
  return stored === null
    || monitorFiringTriggerClaim({ ...stored, monitor_config: decodedMonitorConfig(stored.monitor_config) }) !== 'call_utterance';
}

/**
 * Every `call_utterance` rule whose confidence conditions name no variable the monitor
 * declares as a number, judged against the numeric evaluation variables a surface can see —
 * the REST routes the monitor's stored parameters, the CI push the set it leaves behind. A rule
 * with no confidence condition at all is {@link callUtteranceRuleIssues}'s to report.
 */
export function checkCallUtteranceConfidenceDeclared(
  monitorConfig: unknown,
  numericVariables: readonly string[],
): MonitorRuleIssue[] {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  if (resolveMonitorTrigger(monitorConfig) !== 'call_utterance') return [];
  const rules = (monitorConfig as { rules?: unknown }).rules;
  if (!Array.isArray(rules)) return [];
  const issues: MonitorRuleIssue[] = [];
  rules.forEach((rule, index) => {
    const conditions = callUtteranceConfidenceConditions(rule);
    if (conditions.length === 0) return;
    if (conditions.some((condition) => numericVariables.includes(condition.variable))) return;
    issues.push({
      path: ['rules', index, 'when'],
      message: callUtteranceConfidenceNotDeclaredMessage(conditions[0].variable),
    });
  });
  return issues;
}

/**
 * A MONITOR'S RULES NEED A STRUCTURED ANSWER — on every trigger that reads them.
 *
 * Every rule condition reads a variable the monitor's answer carries, and variables are
 * extracted only from a JSON answer. That holds on every one of {@link MONITOR_RULE_TRIGGERS}:
 * each reaches `interpretMonitorDecision` through the one side-call, which hands it the
 * answer's variables and no system metric, and a `manual` callee's rules read its OWN
 * answer — its caller's findings reach it as context, never as variables. So a monitor on
 * text output answers in prose, no rule can match, and it reads as configured everywhere:
 * - `assistant_reply` — every draft is delivered unjudged (#4447);
 * - `user_message`, `manual` and `call_utterance` — none of the rules' actions ever runs (#4474).
 * A rule-trigger monitor that carries rules must be on `response_format: json_schema`.
 * `idle` is not judged here: rules there are refused outright ({@link monitorRuleKeysOffTrigger}).
 *
 * REFUSED, NOT FLAGGED, like every other required monitor field this file gates
 * (`delay_seconds` on `idle`, rules on a trigger with no runner, a decisions-model
 * binding): a stored config that cannot do what it says is the failure these rules
 * exist to stop.
 *
 * Of the two rule keys, only `rules` needs the answer: a `fallback` alone applies whatever
 * the answer says. `flag_conditions` read the same variables but are not judged by this rule.
 *
 * Reads `response_format` as the column holds it (`text` | `json_schema`), absent being
 * the column's `text` default; the CI push feeds it the value apply would write.
 * `monitor_config` may be the decoded object or the column's JSON TEXT — a raw row read
 * hands over the string, and `resolveMonitorTrigger` would read that as `idle` and pass.
 */
export interface MonitorRulesOutputFields {
  agent_role?: unknown;
  response_format?: unknown;
  monitor_config?: unknown;
}

/** What each surface says — one sentence per trigger, naming what its rules fail to do. */
export function monitorRulesStructuredOutputMessage(trigger: MonitorTrigger): string {
  const [article, consequence] = trigger === 'assistant_reply'
    ? ['an', 'every reply is delivered unjudged']
    : ['a', 'none of their actions runs'];
  return `${article} ${trigger} monitor with rules needs structured output: its rules read variables only `
    + `a JSON answer carries, so on text output no rule can match and ${consequence}. `
    + 'Set response_format to json_schema (wayai push), or remove the rules.';
}

export function monitorRulesStructuredOutputRefusal(agent: MonitorRulesOutputFields): string | null {
  const trigger = refusedRulesTrigger(agent);
  return trigger === null ? null : monitorRulesStructuredOutputMessage(trigger);
}

/** The rule trigger a row is refused on, or null when it is not refused. */
function refusedRulesTrigger(agent: MonitorRulesOutputFields): MonitorTrigger | null {
  if (agent.agent_role !== 'monitor') return null;
  const config = decodedMonitorConfig(agent.monitor_config);
  const trigger = resolveMonitorTrigger(config);
  if (!MONITOR_RULE_TRIGGERS.includes(trigger)) return null;
  const rules = (config as { rules?: unknown }).rules;
  if (!Array.isArray(rules) || rules.length === 0) return null;
  return agent.response_format === 'json_schema' ? null : trigger;
}

/** The column's JSON TEXT parsed, an unreadable one as no config; anything else as given. */
export function decodedMonitorConfig(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * The refusal a WRITE meets: judged on the effective row — each field the write carries
 * over the stored one — and only when the write makes a row that was not refused into one
 * that is. A create (`stored` null) is judged on the write alone. The merge is done here,
 * once, so the agent route, HubDO, the CI diff and the Settings editor cannot come to
 * read a partial write differently.
 *
 * A monitor stored before this rule existed therefore stays editable — renamed, disabled,
 * echoed back by the Settings editor, which sends the stored role and format on every
 * save — instead of every write to it failing; the same leniency the one-monitor-per-
 * trigger rule gives a hub that already holds a pair. It is not left silent: the editor
 * flags it, and each run whose rules could not decide is marked `unjudged` on its audit
 * row (a reply gate's delivery is warned too).
 *
 * THE LENIENCY IS PER TRIGGER: a row counts as already refused only on the trigger it is
 * stored on. Moved onto another rule trigger, still on text output, it becomes a monitor
 * that fails in a new way — a `user_message` monitor whose actions never ran, moved onto
 * the reply gate, becomes a gate that passes every reply unjudged — so that write is
 * judged as new. It was refused before this rule covered `user_message` and `manual`
 * (#4466), and widening the rule must not reopen it.
 */
export function monitorRulesStructuredOutputWriteRefusal(
  stored: MonitorRulesOutputFields | null,
  write: MonitorRulesOutputFields,
): string | null {
  const field = (key: keyof MonitorRulesOutputFields): unknown => (write[key] !== undefined ? write[key] : stored?.[key]);
  const refused = refusedRulesTrigger({
    agent_role: field('agent_role'),
    response_format: field('response_format'),
    monitor_config: field('monitor_config'),
  });
  if (refused === null) return null;
  if (stored && refusedRulesTrigger(stored) === refused) return null;
  return monitorRulesStructuredOutputMessage(refused);
}

/**
 * The action kinds a `user_message` or `manual` monitor's rule may select.
 *
 * `hold` and `rewrite` act on a DRAFTED reply. Neither trigger is the one the draft
 * was judged by: a `user_message` monitor runs before the answering agent, and a
 * `manual` callee — even one a reply gate ran — is not the gate. So they are refused
 * here rather than resolved into an action no runner would carry out.
 *
 * The name keeps `USER_MESSAGE` for the column's sake: `action_kind` has stored these
 * two values since migration v30.
 *
 * `as const satisfies` rather than a widening annotation: the tuple type is what lets
 * `MonitorRuleActionKind` narrow to the kinds an audit row may carry, while
 * `satisfies` still fails compilation if a member stops being an action kind.
 */
export const MONITOR_USER_MESSAGE_ACTION_KINDS = ['none', 'call_tool'] as const satisfies readonly MonitorAction['kind'][];

/**
 * The action kinds an `assistant_reply` monitor's rule may select — the reply gate.
 *
 * `hold` is the gate's own action: the draft is stored for the support team instead of
 * being delivered, and the conversation is flagged and moved to the team track.
 *
 * `rewrite` asks the answering agent ONCE for a revised draft — given its draft, the
 * rule's note and what its tools returned this turn, offered its tools but running none
 * (a tool call holds) — and the gate judges the revision again. A second
 * `rewrite`, a `hold`, or a failure anywhere on that path ends in `hold`, so a rewrite
 * can never deliver text the gate has not passed.
 *
 * ONLY THE ROOT RUN HOLDS. A `manual` callee reached from a reply gate selects from
 * {@link MONITOR_USER_MESSAGE_ACTION_KINDS} like any callee: the draft belongs to the
 * gate that was fired for it.
 */
export const MONITOR_ASSISTANT_REPLY_ACTION_KINDS = ['none', 'call_tool', 'hold', 'rewrite'] as const satisfies readonly MonitorAction['kind'][];

/**
 * The action kinds a `call_utterance` monitor's rule may select — call steering (D8).
 *
 * `steer` appends the rule's NOTE — the hub's own words, verbatim — to the live call's voice
 * session as an instruction. Nothing the monitor produced reaches the session: a note written
 * from the caller's speech would let a caller talk the monitor into instructing the voice.
 * `hold` and `rewrite` have no draft to act on here; the voice's own speech is not a draft.
 */
export const MONITOR_CALL_UTTERANCE_ACTION_KINDS = ['none', 'call_tool', 'steer'] as const satisfies readonly MonitorAction['kind'][];

/**
 * The action kinds an `automation_fire` monitor's rule may select — an automation's fire gate
 * (wayai#4931 D11).
 *
 * FIXED AND TOOL-FREE: the gate runs before the fire, when no conversation exists, so there is
 * no `ToolContext` for a tool to run in and no draft or call to act on. `run` fires as
 * configured; `run_with_agent` fires with the named agent in place of the automation's own;
 * `skip` does not fire. A gate that matches no rule takes its `fallback`, and one with no
 * `fallback` skips — a gate fails closed (`automations/fire-gate.ts`).
 */
export const MONITOR_AUTOMATION_FIRE_ACTION_KINDS = ['run', 'run_with_agent', 'skip'] as const satisfies readonly MonitorAction['kind'][];

/**
 * The action kinds an `automation_item` monitor's rule may select — an automation's item gate
 * (wayai#4931 D11), asked once for each contact of a fire's list.
 *
 * FIXED AND TOOL-FREE, as a fire gate's are: the gate runs before anything is started for the
 * contact, when no conversation exists. `start` starts the contact's work as configured;
 * `start_with_agent` starts it with the named agent in place of the automation's own; `skip`
 * starts nothing for that contact. A gate that matches no rule takes its `fallback`, and one with
 * no `fallback` skips the contact — a gate fails closed (`automations/item-gate.ts`).
 */
export const MONITOR_AUTOMATION_ITEM_ACTION_KINDS = ['start', 'start_with_agent', 'skip'] as const satisfies readonly MonitorAction['kind'][];

/** Every action kind an audit row's `action_kind` may carry. */
export type MonitorRuleActionKind =
  | (typeof MONITOR_USER_MESSAGE_ACTION_KINDS)[number]
  | (typeof MONITOR_ASSISTANT_REPLY_ACTION_KINDS)[number]
  | (typeof MONITOR_CALL_UTTERANCE_ACTION_KINDS)[number];

/**
 * The action kinds a monitor on this trigger may select.
 *
 * THE ONE DECISION the write paths (through {@link collectMonitorRuleIssues}) and the
 * runtime (before it carries a decision out) both take, so an action that saves is an
 * action that runs, and the reverse. A conversation's runner reads it for its own trigger,
 * so a gate's kinds never run there; the fire gate reads it for `automation_fire`.
 */
export function monitorRuleActionKinds(trigger: MonitorTrigger): readonly MonitorAction['kind'][] {
  if (trigger === 'assistant_reply') return MONITOR_ASSISTANT_REPLY_ACTION_KINDS;
  if (trigger === 'call_utterance') return MONITOR_CALL_UTTERANCE_ACTION_KINDS;
  if (trigger === 'automation_fire') return MONITOR_AUTOMATION_FIRE_ACTION_KINDS;
  if (trigger === 'automation_item') return MONITOR_AUTOMATION_ITEM_ACTION_KINDS;
  return MONITOR_USER_MESSAGE_ACTION_KINDS;
}

/** An action kind only an automation gate selects: a fire gate's or an item gate's. */
export type AutomationGateActionKind =
  | (typeof MONITOR_AUTOMATION_FIRE_ACTION_KINDS)[number]
  | (typeof MONITOR_AUTOMATION_ITEM_ACTION_KINDS)[number];

/**
 * Whether `kind` is one of an automation gate's own — the kinds of a gate trigger
 * (`AUTOMATION_GATE_TRIGGERS`): a fire gate's or an item gate's. Neither ever runs on a conversation.
 */
export function isGateActionKind(kind: unknown): kind is AutomationGateActionKind {
  return AUTOMATION_GATE_TRIGGERS.some((trigger) => (monitorRuleActionKinds(trigger) as readonly unknown[]).includes(kind));
}

export function monitorActionKindMessage(kind: string, trigger: MonitorTrigger): string {
  // The list is READ from `monitorRuleActionKinds`, so the message cannot name a
  // different set than the check enforces.
  const allowed = monitorRuleActionKinds(trigger).join(', ');
  if (trigger === 'automation_fire') {
    return `an automation_fire rule cannot select "${kind}": a fire gate runs before any conversation exists, `
      + `so it only decides whether the fire runs. Use one of: ${allowed}.`;
  }
  if (trigger === 'automation_item') {
    return `an automation_item rule cannot select "${kind}": an item gate runs before anything is started for `
      + `the contact, so it only decides whether its work starts. Use one of: ${allowed}.`;
  }
  if (isGateActionKind(kind)) {
    return `a monitor rule cannot select "${kind}": it decides an automation's fire or one contact of it, so only `
      + `an automation_fire or automation_item monitor can select it. Use one of: ${allowed}.`;
  }
  if (trigger === 'assistant_reply') {
    return `an assistant_reply rule cannot select "${kind}". Use one of: ${allowed}.`;
  }
  if (trigger === 'call_utterance') {
    return `a call_utterance rule cannot select "${kind}": there is no drafted reply on a live call. `
      + `Use one of: ${allowed}.`;
  }
  if (kind === 'steer') {
    return 'a monitor rule cannot select "steer": it speaks to a live call\'s voice, so only a '
      + `call_utterance monitor can steer. Use one of: ${allowed}.`;
  }
  // NOT "a user_message rule": `manual` monitors reach this check too, and naming the
  // wrong trigger tells an operator the restriction belongs to an agent they are not
  // editing.
  return `a monitor rule cannot select "${kind}": it acts before the reply exists. Use one of: ${allowed}.`;
}

/**
 * The native tools a monitor rule may call — on `assistant_reply`, narrowed further
 * by `isReplyGateRefusedToolName`, and on `call_utterance` by `isCallRefusedToolName`.
 *
 * AN ALLOWLIST, not a denylist, and the difference is the whole point. A denylist
 * on the rule's own `tool_name` cannot see what the selected tool DISPATCHES
 * (`composed_tools` reaches `close_conversation` by configuration) or what a tool
 * does BEFORE the op it was denied (`consult_agent` completes a model call before
 * it touches `consultOps`). Two such paths were found in review on the first push;
 * an allowlist closes the class by construction rather than by enumeration.
 *
 * A member is here because its whole effect for a rule-executed call was traced and
 * is its own documented one. A tool that reaches further comes OFF this list rather
 * than gaining a gate.
 *
 * THE TWO TRANSFER TOOLS MEET THAT BAR BY A DIFFERENT ROUTE, and the difference is
 * worth reading rather than inferring from their presence.
 *
 *  - Whole effect, traced: each commits the conversation row through
 *    `conversationOps` (the agent for one; responder type, status and team for the
 *    other), writes a durable handoff marker, and returns a RE-ENTRY SIGNAL — a
 *    `reinvoke_intent` for `transfer_to_agent`, a bare `reinvoke` flag and no
 *    intent for `transfer_to_team`. There is no third effect, and neither
 *    dispatches another tool.
 *  - That signal reaches further than `update_state`'s does: it ends the customer's
 *    turn. It does not therefore gain a GATE. The HOST TURN honours it, exiting
 *    before the already-resolved agent answers and re-entering through the same
 *    path a pilot's own transfer uses. Changing who responds IS these tools'
 *    documented effect, so honouring it is the traced effect running rather than a
 *    gate suppressing part of one. {@link MONITOR_RULE_REENTRY_TOOLS} names the set
 *    whose signal the runner reads; every other member's is still dropped.
 *
 * A monitor carries them as DELEGATION rows, and it has no other option: the shipped
 * catalog marks both `is_delegation: true`, `tools.native` rejects a delegation-class
 * native upstream, and the REST install sets the column from that same catalog flag.
 * So `isMonitorRuleCallableTool` admits a delegation row for exactly these two names
 * and keeps refusing every other one — `delegate_to_hub`, `consult_agent` and
 * `start_consult_thread` are refused by this list anyway, so nothing is widened by
 * the narrowing.
 *
 * WHICH TARGET WINS DIFFERS BETWEEN THE TWO, and it is the handlers' own existing
 * behaviour rather than anything a rule changes — but an operator has to know which,
 * so it is stated here and in the public doc rather than left to be discovered:
 *
 *  - `transfer_to_team` takes the row's PRE-BOUND team (`delegated_hub_team_id`) and
 *    falls back to a `team_name` argument only when the row has none. A delegation
 *    row always has one (`target` is required and must resolve), so a rule's
 *    `team_name` on that row is DEAD — the pinned target routes. ON A
 *    `support_model: 'account'` HUB NEITHER ROUTES: the handler resolves the
 *    customer's own `hub_user.hub_team_id`, then `hub.default_hub_team_id`, and
 *    errors if it has neither — the pinned target and the argument are both ignored
 *    by design, for a rule exactly as for a pilot.
 *  - `transfer_to_agent` never reads `delegated_agent_id` at all: it resolves
 *    `agent_name` / `agent_id` from the CALL, exactly as it does for a pilot, so a
 *    rule must name its target and the row's `target` does not constrain it. The
 *    handler still validates the target's track, role and enabled state itself.
 *
 * A rule therefore adds no reach a pilot holding the same row does not already have.
 *
 * A rule's argument values are string, number or boolean
 * (`monitorArgumentSourceSchema`), which does NOT stop a tool with an object or
 * array parameter: `validateAndSanitizeToolParams` parses a JSON-encoded string
 * into the type the tool's schema declares before it type-checks. So
 * `update_state`'s object `updates` is written as a JSON string constant.
 *
 * NON-NATIVE tools — the hub's own external HTTP and MCP tools — are admissible as
 * a CLASS, judged by the tool row rather than by name: they are admin-configured
 * side effects that cannot alter the turn. They are the unbounded wait, which
 * `MONITOR_TOOL_CALL_TIMEOUT_MS` bounds for the customer.
 *
 * `insert_note` MEETS THE BAR BY A THIRD ROUTE, narrower than either above, and
 * it is the only member that never reaches `handleTool` at all.
 *
 *  - Whole effect, traced: the runtime substitutes this monitor's own variables
 *    into an ADMIN-AUTHORED template, escapes the result and prepends it to the
 *    answering agent's last user message for this turn. With `keep_in_history` it
 *    additionally writes one internal `message` row the support team can read.
 *    There is no third effect; it dispatches no tool, calls no model, and reaches
 *    no external system.
 *  - It does not go through the dispatcher. `handleTool` builds a `ToolContext`
 *    against the DO; a note has to reach the HOST TURN's in-memory
 *    `conversationData`, which no `ToolContext` carries. So `runMonitorAction`
 *    carries it out itself and returns it for the turn to apply — the same
 *    return path `MonitorTurnHandover` already travels. That is why membership
 *    here is necessary but not sufficient: `runMonitorAction` decides the
 *    interception FROM THE ROW, never from this name, for the reason
 *    `monitorReentryTracks` gives.
 *  - It is the one member that puts text in front of a model, so its containment
 *    is the escape and the cap rather than a gate: see
 *    {@link MONITOR_NOTE_TEMPLATE_MAX}.
 *
 * `run_monitor` TAKES THE SAME THIRD ROUTE, and is the only member whose effect is
 * ANOTHER MONITOR RUN.
 *
 *  - Whole effect, traced: it runs the named `manual` monitor as a side-call on the
 *    same conversation, with the caller's variables as context. The callee acts on
 *    its OWN rules, and nothing it produces returns to the caller — so a cascade
 *    cannot smuggle a tool the caller may not call: the callee's own rules go
 *    through this same allowlist.
 *  - It does not reach the dispatcher either. Running a callee means re-entering the
 *    side-call carrying this run's depth, chain and root trigger, none of which a
 *    `ToolContext` holds.
 *  - It is the only member that can RECURSE, which is why it is the only one with
 *    depth and cycle guards. See {@link MONITOR_CASCADE_MAX_DEPTH}, which also
 *    records why those two are sufficient.
 *
 * A tool outside all of that is refused as `tool_not_allowed_for_rules`, with
 * `monitorRuleToolNotAllowedMessage`. There are no per-tool explanations here: the
 * list says what is admissible, and what is not is simply absent.
 */
/** The catalog name of the note tool. Spelled once; every reader imports it. */
export const INSERT_NOTE_TOOL_NAME = 'insert_note';

/** The catalog name of the cascade tool. Spelled once; every reader imports it. */
export const RUN_MONITOR_TOOL_NAME = 'run_monitor';

export const MONITOR_RULE_ALLOWED_NATIVE_TOOLS: readonly string[] = [
  'update_state',
  'schedule_followup',
  'transfer_to_agent',
  'transfer_to_team',
  INSERT_NOTE_TOOL_NAME,
  RUN_MONITOR_TOOL_NAME,
];


/**
 * The admitted tools whose RE-ENTRY SIGNAL the rule runner reads instead of dropping.
 *
 * A DERIVED SUBSET, not a second allowlist: a name here that is not in
 * `MONITOR_RULE_ALLOWED_NATIVE_TOOLS` is refused before it runs, so this set can
 * only ever narrow what is already admitted. What it selects is the one behaviour
 * the rest of the allowlist does not have — the turn ENDS, and the conversation
 * re-enters — so it is named rather than inferred from "the tool returned a
 * reinvoke flag": `get_external_files` also returns one, and a rule that reached it
 * must still be dropped rather than silently re-entering the turn to re-read a file.
 *
 * WHY THESE TWO AND NOTHING ELSE: a transfer's whole purpose is to change who
 * responds, and on `user_message` the responding agent was resolved before the
 * monitor ran. Dropping the signal would leave a rule that moves the conversation
 * row and is then answered by the agent it moved it away from.
 */
export const MONITOR_RULE_REENTRY_TOOLS = {
  transfer_to_agent: 'agent',
  transfer_to_team: 'team',
} as const satisfies Record<string, 'agent' | 'team'>;

/**
 * The same table as a Map, which is what the lookup below reads.
 *
 * A plain object's index signature answers for `Object.prototype` members, so
 * `MONITOR_RULE_REENTRY_TOOLS['toString']` would be a FUNCTION and a
 * `!== undefined` test on it would read as "this is a transfer". A `Map` has no
 * such members, which makes the lookup TOTAL for any string — and that totality is
 * the point, because neither caller should have to reason about where the name it
 * passes came from:
 *
 *  - `monitorReentryTracks` (the monitor side-call) reads `row.tool_name` off the
 *    monitor's NON-MCP NATIVE rows. Those names are conventionally the shipped
 *    catalog's, but only conventionally — `createTool` takes `tool_name` from its
 *    caller, so a branching copy or a direct config write is not bound by what the
 *    REST install copies.
 *  - `isMonitorRuleCallableTool`'s delegation branch, where the result is already
 *    conjoined with a prototype-safe `MONITOR_RULE_ALLOWED_NATIVE_TOOLS.includes`.
 *
 * WHAT THIS IS NOT. It is not what stops a tool merely NAMED like a transfer from
 * ending the customer's turn. The rule a customer's message runs through never
 * reaches this table by the name the rule wrote: `monitorReentryTracks` skips
 * `operation === 'mcp_tool_call'` and every non-native row BEFORE reading a name,
 * and `runMonitorAction` then requires `result.reinvoke === true` as well. Those two
 * are the protection; this keeps the remaining lookup honest.
 *
 * This file already models its other name sets this way — see `NATIVE_TOOL_NAMES`.
 */
const MONITOR_RULE_REENTRY_TRACKS: ReadonlyMap<string, MonitorRuleHandoverTrack> =
  new Map(Object.entries(MONITOR_RULE_REENTRY_TOOLS));

/**
 * Which track the conversation is on once this tool has run.
 *
 * Carried in the SAME declaration as the set rather than as a second table, so the
 * membership and the track cannot drift: a name admitted here without a track does
 * not compile, and a track with no name is unreachable.
 */
export type MonitorRuleHandoverTrack =
  (typeof MONITOR_RULE_REENTRY_TOOLS)[keyof typeof MONITOR_RULE_REENTRY_TOOLS];

/**
 * Does this admitted tool end the turn and re-enter the conversation — and on which
 * track? `undefined` for every other tool, whose re-entry signal stays dropped.
 */
export function monitorRuleReentryTrack(toolName: string): MonitorRuleHandoverTrack | undefined {
  return MONITOR_RULE_REENTRY_TRACKS.get(toolName);
}

export function monitorRuleToolNotAllowedMessage(toolName: string, trigger: MonitorTrigger): string {
  if (trigger === 'call_utterance') {
    const callable = MONITOR_RULE_ALLOWED_NATIVE_TOOLS.filter((name) => !isCallRefusedToolName(name));
    return `a call_utterance rule cannot call "${toolName}": it acts on a live call without the caller `
      + 'confirming anything, so it may call only a tool with no side effects — '
      + `${callable.join(', ')}, or this hub's own HTTP tools that only read (GET, HEAD or OPTIONS). `
      + 'MCP tools, and tools whose effects are unknown, are refused.';
  }
  if (trigger === 'assistant_reply' && isReplyGateRefusedToolName(toolName)) {
    return `an assistant_reply rule cannot call "${toolName}": the reply already exists when `
      + 'this monitor runs, so there is no turn left for it to act on. Put it on a '
      + 'user_message monitor instead, which runs before the answering agent.';
  }
  // The list a rule on THIS trigger can actually reach, so a reply-gate operator is
  // not told they may call the very tool the gate refuses.
  const callable = trigger === 'assistant_reply'
    ? MONITOR_RULE_ALLOWED_NATIVE_TOOLS.filter((name) => !isReplyGateRefusedToolName(name))
    : MONITOR_RULE_ALLOWED_NATIVE_TOOLS;
  return `a ${trigger} rule cannot call "${toolName}". Rules can call `
    + `${callable.join(', ')}, and this hub's own external `
    + 'HTTP and MCP tools. Assign anything else to the answering agent instead.';
}

/**
 * The admitted tools a rule may NOT call once the reply exists — when the monitor
 * running it was fired by `assistant_reply`, or is a callee reached from one.
 *
 * TWO, each for a reason about the draft rather than about the tool:
 *
 *  - `transfer_to_agent` — its whole effect is a CONTINUATION: the conversation
 *    re-enters and the target agent answers. After a draft that is being delivered,
 *    that is a second AI reply to one customer message (owner decision 10, D). Every
 *    OTHER tool's re-entry signal is simply dropped on this trigger; this one has no
 *    effect left once it is dropped except moving the row away from the agent whose
 *    reply is still going out, so it is refused instead of silently half-run.
 *    Derived from {@link MONITOR_RULE_REENTRY_TOOLS} — every tool on the `agent`
 *    track — rather than spelled, so a second agent-track tool is refused by the
 *    same rule. `transfer_to_team` stays admitted: its effect is the row, which is
 *    still true after a delivered reply, and it produces no continuation.
 *  - `insert_note` — a note briefs the agent ABOUT to reply. There is none. Refused
 *    from the config at save time; a `manual` callee reached from a reply gate is
 *    refused at run time as `note_outside_user_message_root`, which the save cannot
 *    decide because a callee's caller is unknown when it is saved.
 */
function isReplyGateRefusedToolName(toolName: string): boolean {
  return monitorRuleReentryTrack(toolName) === 'agent' || toolName === INSERT_NOTE_TOOL_NAME;
}

/**
 * The admitted tools a `call_utterance` rule may NOT call (coordinator decision on P1-13,
 * gh #4418): a live call's rule runs with nobody confirming it — a monitor's actions reach
 * `handleTool` without the call path's spoken-confirmation guard — so it may call only a tool
 * the side-effect classification (owner decision 20, Q5) calls `none`. A HAND-OFF is such a
 * tool: both transfers are `none` in the catalog, and a transfer to the team ends the call
 * through the conversation's own writer. `insert_note` is `none` too and is still refused: it
 * briefs an agent about to reply, and a call's check has no reply.
 *
 * The native half, decided from the catalog by name; `isMonitorRuleCallableTool` asks the
 * same classification of an HTTP or MCP row.
 */
function isCallRefusedToolName(toolName: string): boolean {
  return toolName === INSERT_NOTE_TOOL_NAME || catalogToolSideEffects(toolName) !== 'none';
}

/**
 * The side-effect classification (owner decision 20, Q5) of a tool ROW as a rule would run
 * it: an MCP row is unknown; a platform native by its catalog entry; an HTTP row by its
 * method; and either raised to its composed chain, as the call path classifies a call. The
 * one reading `isMonitorRuleCallableTool` takes for `call_utterance`, so a rule that saves is
 * a rule that runs.
 */
export function monitorRuleRowSideEffects(row: {
  is_native?: unknown;
  operation?: unknown;
  tool_name?: unknown;
  tool_method?: unknown;
  composed_tools?: unknown;
}): ToolSideEffects {
  if (row.operation === 'mcp_tool_call') return 'unknown';
  const isNative = row.is_native === true || row.is_native === 1;
  const own = isNative
    ? catalogToolSideEffects(typeof row.tool_name === 'string' ? row.tool_name : '')
    : httpToolSideEffects(row.tool_method);
  return withComposedChain(own, row.composed_tools);
}

/**
 * May a rule call this tool ROW?
 *
 * THE ONE PREDICATE all three surfaces share — the REST write, the CI push's
 * resulting set, and the run-time check — because a rule that validates on one and
 * is refused by another is the divergence every shared helper in this file exists
 * to prevent.
 *
 * Decided from the ROW, never from the name's shape. Which FIELD decides differs by
 * kind, and that is why the order below matters: `operation` is what identifies an
 * MCP assignment, because `is_native` does not — an MCP row carries `true` there.
 * `is_native` then separates a hub-configured external tool from a platform one,
 * and `is_delegation` marks a delegation row, which is native whatever it is
 * called.
 *
 * A DELEGATION ROW IS REFUSED UNLESS IT IS A RE-ENTRY TOOL, and that exception is
 * not a loosening: the transfer tools have no other row shape. The catalog marks
 * both `is_delegation: true`, the CI push rejects a delegation-class native declared
 * under `tools.native` before it reaches a payload, and the REST install copies the
 * catalog flag — so refusing every delegation row would leave the two names on
 * `MONITOR_RULE_ALLOWED_NATIVE_TOOLS` unreachable by any hub. The name is still
 * checked against that list, so the exception admits exactly what the list already
 * did and nothing more.
 */
export function isMonitorRuleCallableTool(
  row: {
    is_native?: unknown;
    is_delegation?: unknown;
    operation?: unknown;
    tool_name?: unknown;
    /** Read on `call_utterance` only: an HTTP row's method. */
    tool_method?: unknown;
    /** Read on `call_utterance` only: the chain the call would carry. */
    composed_tools?: unknown;
  },
  /**
   * The trigger the rule runs UNDER. At save time that is the monitor's own; at run
   * time it is the cascade's ROOT, because a `manual` callee inherits its caller's
   * position in the turn. REQUIRED, so no surface can ask the question without
   * saying where the rule sits.
   */
  trigger: MonitorTrigger,
): boolean {
  // ON A LIVE CALL, only a tool with no side effects (`isCallRefusedToolName`), judged by the
  // one classification — which reads an MCP row as unknown before it reads nativeness, for
  // the reason the order below states. On top of, never instead of, what every trigger asks.
  if (trigger === 'call_utterance' && monitorRuleRowSideEffects(row) !== 'none') return false;
  // MCP FIRST, and this order is the whole correctness of the function: an MCP
  // assignment row is written with `is_native: true` (`buildMcpToolAssignment`,
  // whose `isMcpToolAssignment` is the backend-side spelling of this same test),
  // so testing nativeness first would refuse every MCP tool — while the message
  // beside this list promises they are callable.
  if (row.operation === 'mcp_tool_call') return true;
  const isNative = row.is_native === true || row.is_native === 1;
  if (!isNative) return true;
  const name = typeof row.tool_name === 'string' ? row.tool_name : '';
  const onAllowlist = MONITOR_RULE_ALLOWED_NATIVE_TOOLS.includes(name)
    && !(trigger === 'call_utterance' && isCallRefusedToolName(name));
  if (row.is_delegation === true || row.is_delegation === 1) {
    // BOTH, so the docblock's claim is structural rather than a test's: the
    // exception admits exactly what the allowlist already named.
    const track = monitorRuleReentryTrack(name);
    // UNDER A REPLY GATE, an `agent`-track transfer is refused — see
    // `isReplyGateRefusedToolName`. Decided here, on the ROW's delegation branch,
    // because that is the only row shape the tool has.
    if (trigger === 'assistant_reply' && track === 'agent') return false;
    return onAllowlist && track !== undefined;
  }
  return onAllowlist;
}

/**
 * Is this the name of a native tool a rule may NOT call?
 *
 * The half of admissibility decidable from the config alone, so `wayai push` and a
 * REST body are refused at the contract rather than only where the hub is visible.
 * A name the native catalog does not know belongs to an external or MCP tool, whose
 * admissibility is a row question — answered by `isMonitorRuleCallableTool`.
 */
export function isRefusedNativeToolName(toolName: string, trigger: MonitorTrigger): boolean {
  if (!NATIVE_TOOL_NAMES.has(toolName)) return false;
  if (!MONITOR_RULE_ALLOWED_NATIVE_TOOLS.includes(toolName)) return true;
  if (trigger === 'call_utterance') return isCallRefusedToolName(toolName);
  return trigger === 'assistant_reply' && isReplyGateRefusedToolName(toolName);
}

/**
 * Every `call_tool` a config selects, with the path to report it at.
 *
 * Walks `rules` and `fallback` together because both select an action and both are
 * gated identically. Tolerant of shapes the schema would reject — the column is
 * `.passthrough()` and the CI path carries no object schema, so this runs over
 * whatever was stored.
 */
export function monitorRuleToolSelections(
  monitorConfig: unknown,
): Array<{ toolName: string; path: (string | number)[] }> {
  return monitorRuleActions(monitorConfig).flatMap(({ action, path }) => {
    const { kind, tool_name: toolName } = action as { kind?: unknown; tool_name?: unknown };
    if (kind !== 'call_tool' || typeof toolName !== 'string' || toolName === '') return [];
    return [{ toolName, path: [...path, 'tool_name'] }];
  });
}

/**
 * Each action a config selects, with the path it sits at.
 *
 * One walk for both gated questions — which KIND was selected and which TOOL —
 * because two walks over the same two places is how `rules` and `fallback` end up
 * gated differently from each other.
 */
function monitorRuleActions(
  monitorConfig: unknown,
): Array<{ action: Record<string, unknown>; path: (string | number)[] }> {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  const config = monitorConfig as { rules?: unknown; fallback?: unknown };
  const found: Array<{ action: Record<string, unknown>; path: (string | number)[] }> = [];

  const visit = (action: unknown, path: (string | number)[]): void => {
    if (!action || typeof action !== 'object' || Array.isArray(action)) return;
    found.push({ action: action as Record<string, unknown>, path });
  };

  if (Array.isArray(config.rules)) {
    config.rules.forEach((rule, index) => {
      if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return;
      visit((rule as { action?: unknown }).action, ['rules', index, 'action']);
    });
  }
  visit(config.fallback, ['fallback']);
  return found;
}

/** One rejected thing, with the path a write surface reports it at. */
export interface MonitorRuleIssue {
  path: (string | number)[];
  message: string;
}

export const AUTOMATION_FIRE_FLAG_CONDITIONS_MESSAGE =
  'an automation_fire monitor decides an automation\'s fire before any conversation exists, so there is no '
  + 'conversation for it to flag. Remove flag_conditions.';

export const AUTOMATION_FIRE_INPUT_SHAPING_MESSAGE =
  'an automation_fire monitor reads the automation and its fire, never a conversation\'s messages, so it has '
  + 'no history window to size. Remove this key.';

export const AUTOMATION_ITEM_FLAG_CONDITIONS_MESSAGE =
  'an automation_item monitor decides one contact of an automation\'s fire before anything is started for it, '
  + 'so there is no conversation for it to flag. Remove flag_conditions.';

export const AUTOMATION_ITEM_INPUT_SHAPING_MESSAGE =
  'an automation_item monitor reads the automation, its fire and one contact, never a conversation\'s messages, '
  + 'so it has no history window to size. Remove this key.';

/** Each automation gate's refusals of the keys only a conversation's monitor reads. */
const AUTOMATION_GATE_KEY_MESSAGES: Readonly<Record<AutomationGateTrigger, { flagConditions: string; inputShaping: string }>> = {
  automation_fire: { flagConditions: AUTOMATION_FIRE_FLAG_CONDITIONS_MESSAGE, inputShaping: AUTOMATION_FIRE_INPUT_SHAPING_MESSAGE },
  automation_item: { flagConditions: AUTOMATION_ITEM_FLAG_CONDITIONS_MESSAGE, inputShaping: AUTOMATION_ITEM_INPUT_SHAPING_MESSAGE },
};

/**
 * Every refusal only an automation gate's monitor (`automation_fire`, `automation_item`) meets
 * that is decidable from the config alone: `flag_conditions` (a gate has no conversation to flag)
 * and the two keys that shape a conversation's history window (a gate has none). Reported by
 * {@link collectMonitorRuleIssues}, so both write surfaces and the Settings editor meet them
 * through the function they already call. Its rules' action kinds are
 * {@link MONITOR_AUTOMATION_FIRE_ACTION_KINDS} or {@link MONITOR_AUTOMATION_ITEM_ACTION_KINDS},
 * judged there too.
 */
export function automationGateIssues(monitorConfig: unknown): MonitorRuleIssue[] {
  if (!monitorConfig || typeof monitorConfig !== 'object') return [];
  const trigger = resolveMonitorTrigger(monitorConfig);
  if (!isAutomationGateTrigger(trigger)) return [];
  const messages = AUTOMATION_GATE_KEY_MESSAGES[trigger];
  const config = monitorConfig as Record<string, unknown>;
  const issues: MonitorRuleIssue[] = [];
  if (Array.isArray(config.flag_conditions) && config.flag_conditions.length > 0) {
    issues.push({ path: ['flag_conditions'], message: messages.flagConditions });
  }
  for (const key of MONITOR_INPUT_SHAPING_KEYS) {
    if (config[key] !== undefined) issues.push({ path: [key], message: messages.inputShaping });
  }
  return issues;
}

/**
 * Everything wrong with a config's decision keys that is decidable from the config
 * ALONE — the trigger that cannot read them, an action kind with no runner, a
 * NATIVE tool name outside `MONITOR_RULE_ALLOWED_NATIVE_TOOLS`, and a live call's own
 * refusals (`callUtteranceRuleIssues`).
 *
 * DEFAULT-DENY, which is the property a reader needs most: every native the
 * allowlist does not name is refused here, whether or not this turn could have
 * honoured it. A name the native catalog does not know belongs to an external or
 * MCP tool and passes, because its admissibility is a question about the tool ROW —
 * `isMonitorRuleCallableTool` answers that one, where the hub is visible.
 *
 * Whether the monitor CARRIES the tool it names is a third question, also needing
 * the hub: `checkMonitorRuleToolsAssigned` answers it, and each write surface feeds
 * it the tool list it can see.
 */
export function collectMonitorRuleIssues(monitorConfig: unknown): MonitorRuleIssue[] {
  const offTrigger = monitorRuleKeysOffTrigger(monitorConfig)
    .map((key) => ({ path: [key], message: MONITOR_RULE_TRIGGER_MESSAGE }));
  // The keys are unreadable on this trigger; what they select is then moot, and
  // reporting both would tell an operator to fix an action on a key the same
  // response has just refused.
  if (offTrigger.length > 0) return offTrigger;

  // ONE walk, which is what `monitorRuleActions` is for: asking the two questions
  // over two separate traversals is how `rules` and `fallback` end up gated
  // differently from each other.
  // The monitor's OWN trigger. A `manual` monitor's rules are judged as a callee's
  // anywhere in a cascade; what its ROOT forbids is decided at run time.
  const trigger = resolveMonitorTrigger(monitorConfig);
  const kinds: readonly string[] = monitorRuleActionKinds(trigger);
  // A live call's refusals first — among them its `fallback`, whose action is then moot. An
  // automation gate's own refusals beside them.
  const issues: MonitorRuleIssue[] = [...callUtteranceRuleIssues(monitorConfig), ...automationGateIssues(monitorConfig)];
  for (const { action, path } of monitorRuleActions(monitorConfig)) {
    if (trigger === 'call_utterance' && path[0] === 'fallback') continue;
    const kind = action.kind;
    if (typeof kind === 'string' && !kinds.includes(kind)) {
      issues.push({ path: [...path, 'kind'], message: monitorActionKindMessage(kind, trigger) });
    }
    const toolName = action.tool_name;
    if (kind === 'call_tool' && typeof toolName === 'string' && isRefusedNativeToolName(toolName, trigger)) {
      issues.push({ path: [...path, 'tool_name'], message: monitorRuleToolNotAllowedMessage(toolName, trigger) });
      // The tool is refused outright, so its arguments are moot — reporting both
      // would tell an operator to fix an argument of a call the same response refused.
      continue;
    }
    if (kind === 'call_tool' && toolName === INSERT_NOTE_TOOL_NAME) {
      issues.push(...insertNoteArgumentIssues(action, path));
    }
    // The callee's NAME is decidable from config alone; whether it resolves to a
    // runnable `manual` monitor is a hub question, answered by
    // `checkMonitorCalleesRunnable` where the agent list is visible.
    if (kind === 'call_tool' && toolName === RUN_MONITOR_TOOL_NAME) {
      const callee = readRunMonitorCallee(action);
      if (!callee.ok) {
        issues.push({
          path: [...path, 'args', 'monitor_name'],
          message: runMonitorCalleeMessage(callee.reason),
        });
      }
    }
  }
  return issues;
}

/** Why an `insert_note` action carries no usable template. */
export type InsertNoteTemplateRefusal =
  | 'note_template_missing'
  | 'note_template_not_const'
  | 'note_template_empty'
  | 'note_template_too_long';

export type InsertNoteTemplate =
  | { ok: true; template: string }
  | { ok: false; reason: InsertNoteTemplateRefusal };

/**
 * The admin-authored note text an `insert_note` action carries, or why it has none.
 *
 * THE ONE DECISION both edges take — the write paths through
 * {@link collectMonitorRuleIssues}, and the runtime before it renders anything.
 * Two copies of this rule is how a note that saves comes to be refused when it
 * fires, or worse, the reverse; the rest of this file states that property for the
 * tool allowlist and the assigned-tool list for the same reason.
 *
 * THE `const` RULE IS THE SECURITY PROPERTY, not a convenience or a schema tidy-up.
 * `insert_note` is the only thing in the rule system whose output becomes TEXT IN
 * FRONT OF THE ANSWERING MODEL, so its sentence is ADMIN-AUTHORED by construction.
 * A `from_variable` template would be model-authored text — derived from the
 * customer's own message on a structured-output monitor — promoted into the pilot's
 * context as if the administrator had written it. The design record defers
 * model-authored note text as its own decision ("wrapped and labelled as data like
 * any tool output"); until that ships the shape is refused.
 *
 * WHY THE RUNTIME RE-ASKS rather than trusting the write gate: the resolved action
 * the interpreter hands the runner cannot answer this question at all. Both
 * `{ const: 'text' }` and `{ from_variable: 'x' }` resolve to a plain string in
 * `ResolvedMonitorAction['args']`, so provenance is only visible in the RAW config
 * — which is also reachable by paths the write gate does not cover.
 *
 * Variables still reach the note, by `{{name}}` substitution into the admin's own
 * sentence, escaped on the way in. What cannot happen is the whole note being
 * handed to the model.
 */
export function readInsertNoteTemplate(action: unknown): InsertNoteTemplate {
  const read = readConstStringArg(action, 'template');
  if (!read.ok) {
    return {
      ok: false,
      reason: read.reason === 'missing' ? 'note_template_missing'
        : read.reason === 'not_const' ? 'note_template_not_const'
          : 'note_template_empty',
    };
  }
  if (read.value.length > MONITOR_NOTE_TEMPLATE_MAX) {
    return { ok: false, reason: 'note_template_too_long' };
  }
  return { ok: true, template: read.value };
}

/**
 * One argument of a rule's action, required to be a `const` string.
 *
 * THE ONE PLACE the `{ const: … }` unwrap is spelled. `readInsertNoteKeepInHistory`'s
 * docblock already warned that a hand-rolled second copy would be "free to drift";
 * the callee reader made it a third, so the shape moved here and its three readers
 * map the reason onto their own vocabulary.
 *
 * The `const`-only rule is the same security property in both callers, stated at
 * each: an argument the model could supply would let its output choose what the
 * configuration does, rather than produce a value the configuration interprets.
 */
function readConstStringArg(
  action: unknown,
  argName: string,
): { ok: true; value: string } | { ok: false; reason: 'missing' | 'not_const' | 'empty' } {
  const args = (action && typeof action === 'object')
    ? (action as Record<string, unknown>).args
    : undefined;
  const arg = (args && typeof args === 'object')
    ? (args as Record<string, unknown>)[argName]
    : undefined;

  if (arg === undefined) return { ok: false, reason: 'missing' };

  const constValue = (arg && typeof arg === 'object' && !Array.isArray(arg))
    ? (arg as Record<string, unknown>).const
    : undefined;

  if (typeof constValue !== 'string') return { ok: false, reason: 'not_const' };
  if (constValue.trim() === '') return { ok: false, reason: 'empty' };
  return { ok: true, value: constValue };
}

/**
 * Does this `insert_note` action ask for the note to be KEPT as an internal row?
 *
 * Beside {@link readInsertNoteTemplate} because both of the tool's arguments are
 * read off the same raw action, by the same shared helper — a reader hand-rolled at
 * the runtime would be a second place the `{ const: … }` unwrap is spelled, free to
 * drift from the one the write paths use.
 *
 * ⚠ NOT THE SAME `keep_in_history` AS A TOOL ROW'S. A tool row's
 * `execution_config.keep_in_history` decides whether that tool's call/result rows
 * stay in the LLM WINDOW. THIS decides whether the note is written as a `message`
 * row at all — a record for the support team. The row a caller writes for it sets
 * the COLUMN to false regardless: a brief describes the message it was written for,
 * and kept in the window it would be re-read as a live instruction on every later
 * turn.
 *
 * A LITERAL `{ const: true }` AND NOTHING ELSE. Absent, false, a string, or a
 * `from_variable` mapping all mean ephemeral — the default the design states — so a
 * config this cannot read keeps the cheaper, more private behaviour rather than
 * persisting a row nobody asked for.
 */
export function readInsertNoteKeepInHistory(action: unknown): boolean {
  const args = (action && typeof action === 'object')
    ? (action as Record<string, unknown>).args
    : undefined;
  const source = (args && typeof args === 'object')
    ? (args as Record<string, unknown>).keep_in_history
    : undefined;
  if (!source || typeof source !== 'object' || Array.isArray(source)) return false;
  return (source as { const?: unknown }).const === true;
}

/**
 * The maximum depth of a monitor cascade.
 *
 * 3, and the same number as {@link MAX_REINVOKE_DEPTH} in `tools/utils/reinvoke.ts`
 * because it is the same question — how many times may one customer message spawn
 * another unit of work before we stop believing the configuration meant it. That is
 * the precedent this guard follows, NOT `consult_agent`, which has no depth guard
 * and no cycle detection at all (its guards are self-name equality, advisor role and
 * enabled).
 *
 * Depth 0 is the monitor the turn itself selected; a callee it runs is depth 1. So
 * this admits a chain of three callees below the root and refuses the fourth.
 *
 * THIS IS THE ONLY BOUND ON CASCADE WORK, and it is sufficient only because a run
 * resolves exactly one action — see the note below on the removed run budget.
 */
export const MONITOR_CASCADE_MAX_DEPTH = 3;

/**
 * WHY THERE IS NO SEPARATE RUN BUDGET, which a reader will expect beside the depth.
 *
 * One was written and REMOVED as dead: local review showed it could never fire.
 * `interpretMonitorDecision` resolves at most ONE action per monitor run — first
 * matching rule, else the fallback — and `runMonitorAction` carries out that one
 * action. So a run can invoke at most one callee, a cascade is a strict CHAIN
 * rather than a tree, and the number of callee runs in a turn is exactly bounded by
 * {@link MONITOR_CASCADE_MAX_DEPTH}. A second counter set above that ceiling was a
 * guard that read as present and enforced nothing — with a test that passed without
 * ever reaching it.
 *
 * WHAT WOULD BRING IT BACK: a run resolving more than one action. Then a cascade
 * becomes a tree, depth stops bounding total work, and a per-turn counter is needed
 * again. The property this rests on is pinned by `spends one callee run per level,
 * because a run resolves one action` in `conversation-monitor-rules.do.test.ts`, so
 * that change goes red rather than silently unbounding the customer's turn.
 */

/** Why a `run_monitor` action names no usable callee. */
export type RunMonitorCalleeRefusal =
  | 'callee_missing'
  | 'callee_not_const'
  | 'callee_empty';

export type RunMonitorCallee =
  | { ok: true; name: string }
  | { ok: false; reason: RunMonitorCalleeRefusal };

/**
 * The callee an admin named on a `run_monitor` action, or why it names none.
 *
 * THE ONE DECISION both edges take — the write paths through
 * {@link collectMonitorRuleIssues}, and the runtime before it resolves anything —
 * for the reason the rest of this file repeats: two copies is how a rule that saves
 * comes to be refused when it fires.
 *
 * `const` ONLY, like the note's template and for a related reason. The callee
 * decides WHICH MONITOR RUNS, so letting it come `from_variable` would let a model's
 * output choose the code path — the model would be naming a target rather than
 * producing a value the admin's configuration interprets, which is the property
 * `interpretMonitorDecision`'s docblock exists to keep ("the model never names a
 * tool and never supplies an argument the config did not ask for"). It is also what
 * makes the callee resolvable at SAVE time, where a non-`manual` target is rejected.
 *
 * A NAME, not an id (owner decision 9). Hub-relative, so it survives every copy path
 * unchanged — see `RUN_MONITOR_TOOL_NAME`'s catalog entry for why that removed a
 * whole PR of remapping. The cost is that a renamed callee stops resolving, which
 * the run-time re-check turns into a recorded refusal rather than a broken turn.
 */
export function readRunMonitorCallee(action: unknown): RunMonitorCallee {
  const read = readConstStringArg(action, 'monitor_name');
  if (read.ok) return { ok: true, name: read.value };
  return {
    ok: false,
    reason: read.reason === 'missing' ? 'callee_missing'
      : read.reason === 'not_const' ? 'callee_not_const'
        : 'callee_empty',
  };
}

/** What an operator is told about each callee refusal. */
export function runMonitorCalleeMessage(reason: RunMonitorCalleeRefusal): string {
  switch (reason) {
    case 'callee_missing':
      return 'run_monitor needs a monitor_name: the monitor to run.';
    case 'callee_not_const':
      return 'run_monitor\'s monitor_name must be a const you write — it chooses which '
        + 'monitor runs, so it cannot come from a variable.';
    case 'callee_empty':
      return 'run_monitor\'s monitor_name is empty — name the monitor to run.';
  }
}

/** Every callee a config names, with the path to report each at. */
export function monitorCalleeSelections(
  monitorConfig: unknown,
): Array<{ callee: RunMonitorCallee; path: (string | number)[] }> {
  return monitorRuleActions(monitorConfig).flatMap(({ action, path }) => {
    const { kind, tool_name: toolName } = action as { kind?: unknown; tool_name?: unknown };
    if (kind !== 'call_tool' || toolName !== RUN_MONITOR_TOOL_NAME) return [];
    return [{ callee: readRunMonitorCallee(action), path: [...path, 'args', 'monitor_name'] }];
  });
}

/**
 * Whether a resolved callee agent may actually be run, or why not.
 *
 * THE SAME QUESTION AT BOTH EDGES — the write paths ask it of the agent set a save
 * would leave behind, and the runtime asks it of the hub config it holds — so it
 * takes a plain row rather than either side's types. `null` means runnable.
 *
 * The order is the order an operator can act on: a name that resolves to nothing is
 * a different fix from a monitor that is disabled.
 */
export function monitorCalleeRefusal(
  callee: { agent_role?: unknown; enabled?: unknown; monitor_config?: unknown } | null | undefined,
  name: string,
): { reason: MonitorCalleeRunRefusal; message: string } | null {
  if (!callee) {
    return {
      reason: 'callee_not_found',
      message: `no agent named "${name}" on this hub — run_monitor names a monitor on the same hub.`,
    };
  }
  if (callee.agent_role !== 'monitor') {
    return {
      reason: 'callee_not_monitor',
      message: `"${name}" is not a monitor (its role is "${String(callee.agent_role ?? 'unset')}").`,
    };
  }
  if (!isEnabledAgentValue(callee.enabled)) {
    return {
      reason: 'callee_disabled',
      message: `the monitor "${name}" is disabled — enable it, or remove the rule that runs it.`,
    };
  }
  const trigger = resolveMonitorTrigger(callee.monitor_config);
  if (trigger !== 'manual') {
    return {
      reason: 'callee_not_manual',
      message: `the monitor "${name}" has trigger "${trigger}" — run_monitor can only run a `
        + 'monitor whose trigger is manual, so that nothing else fires it.',
    };
  }
  return null;
}

/** Why a named callee could not be run. Shared by the write paths and the runtime. */
export type MonitorCalleeRunRefusal =
  | 'callee_not_found'
  | 'callee_not_monitor'
  | 'callee_disabled'
  | 'callee_not_manual';

/** What an operator is told about each note-template refusal. */
export function insertNoteTemplateMessage(reason: InsertNoteTemplateRefusal): string {
  switch (reason) {
    case 'note_template_missing':
      return 'insert_note needs a template: the note text to put in front of the answering agent.';
    case 'note_template_not_const':
      return 'insert_note\'s template must be a const string you write — the answering agent '
        + 'reads it as instructions, so it cannot come from a variable. Use '
        + '{{variable_name}} inside it to substitute this monitor\'s variables.';
    case 'note_template_empty':
      return 'insert_note\'s template is empty — remove the rule, or give it something to say.';
    case 'note_template_too_long':
      return `insert_note's template is longer than the ${MONITOR_NOTE_TEMPLATE_MAX}-character limit.`;
  }
}

/**
 * The action a decision SELECTED, read back off the raw config.
 *
 * `matched_rule_index` is the interpreter's own output: an index into `rules`, or
 * `null` when `fallback` supplied the action. Reading the action back rather than
 * threading the raw one through the interpreter keeps `ResolvedMonitorAction` what
 * its docblock says it is — what each argument IS, not where it came from.
 */
export function monitorSelectedAction(
  monitorConfig: unknown,
  matchedRuleIndex: number | null,
): unknown {
  if (!monitorConfig || typeof monitorConfig !== 'object') return undefined;
  const config = monitorConfig as { rules?: unknown; fallback?: unknown };
  if (matchedRuleIndex === null) return config.fallback;
  if (!Array.isArray(config.rules)) return undefined;
  const rule = config.rules[matchedRuleIndex];
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return undefined;
  return (rule as { action?: unknown }).action;
}

/**
 * The note-template issues a write surface reports, off {@link readInsertNoteTemplate}.
 *
 * DECIDED FROM THE NAME here, which is the approximation this whole gate already
 * makes: `isRefusedNativeToolName` treats a native name as the native tool, because
 * config alone cannot see tool ROWS. The consequence is the one that decision
 * already carries — a hub whose own external tool is named `insert_note` has such a
 * rule refused, with a message naming the exact argument. The RUNTIME does not
 * share the approximation: it decides the interception from the row, so that hub's
 * tool still runs as the ordinary tool it is.
 */
function insertNoteArgumentIssues(
  action: Record<string, unknown>,
  path: (string | number)[],
): MonitorRuleIssue[] {
  const result = readInsertNoteTemplate(action);
  if (result.ok) return [];
  return [{
    path: [...path, 'args', 'template'],
    message: insertNoteTemplateMessage(result.reason),
  }];
}

/**
 * The tool names an agent CARRIES, spelled the way `getToolConfig` matches them.
 *
 * BOTH spellings — a row's `tool_name` and its `tool_display_name` — because
 * `getToolConfig` resolves either, so a rule naming the display name runs and a
 * check that knew only `tool_name` would refuse a rule that works.
 *
 * ONE function because four surfaces ask this question and must agree: the REST
 * routes, the CI push's resulting set, the side-call that hands the list to the
 * interpreter, and the tests that pin them. Three independent copies are how the
 * save-time gate and the run-time gate come to disagree — which is the failure
 * those gates exist to prevent.
 *
 * Rows arrive untyped: the REST route reads them off an RPC, the CI path off the
 * diff engine's own row type.
 */
export function agentAssignedToolNames(
  rows: readonly Record<string, unknown>[],
  agentId: string | null | undefined,
): string[] {
  if (!agentId) return [];
  const names: string[] = [];
  for (const row of rows) {
    if (row.agent_id !== agentId) continue;
    if (typeof row.tool_name === 'string' && row.tool_name !== '') names.push(row.tool_name);
    if (typeof row.tool_display_name === 'string' && row.tool_display_name !== '') {
      names.push(row.tool_display_name);
    }
  }
  return names;
}

/**
 * The tools a rule names that the monitor does not carry.
 *
 * `assignedTools` is the monitor's own tool list, spelled the way `getToolConfig`
 * matches at run time: a row's `tool_name` OR its `tool_display_name`, either of
 * which may be the name the rule wrote. A surface passing only one of the two
 * would refuse a rule that runs.
 *
 * Separate from `collectMonitorRuleIssues` because the answer is not in the config:
 * the REST routes read the hub's tool rows, and the CI push judges the set its own
 * push is about to leave behind.
 */
export function checkMonitorRuleToolsAssigned(
  monitorConfig: unknown,
  assignedTools: readonly string[],
): MonitorRuleIssue[] {
  return monitorRuleToolSelections(monitorConfig)
    .filter(({ toolName }) => !assignedTools.includes(toolName))
    .map(({ toolName, path }) => ({ path, message: monitorUnassignedToolMessage(toolName) }));
}

/**
 * Every `run_monitor` rule whose callee the hub cannot run, judged against an agent
 * set the caller supplies.
 *
 * THE AGENT SET IS THE CALLER'S TO CHOOSE, and the two callers choose differently:
 * the CI push passes what the push would LEAVE BEHIND, so creating a caller and its
 * callee together lands (the discipline `findMonitorTriggerClashes` already
 * applies); the REST routes pass the hub's agents AS STORED, because a single-agent
 * write cannot see a set. That asymmetry is why this check is a courtesy at the
 * write edge and not the guarantee — the guarantee is the run-time re-check, which
 * is the only reader that sees the configuration as it actually fires.
 *
 * Only the config's own callee NAMES are read here; whether each name is even a
 * `const` was already answered by `collectMonitorRuleIssues`, so a malformed one is
 * skipped rather than reported twice.
 */
export function checkMonitorCalleesRunnable(
  monitorConfig: unknown,
  agents: readonly { agent_name?: unknown; agent_role?: unknown; enabled?: unknown; monitor_config?: unknown }[],
): MonitorRuleIssue[] {
  const issues: MonitorRuleIssue[] = [];
  for (const { callee, path } of monitorCalleeSelections(monitorConfig)) {
    if (!callee.ok) continue;
    const row = agents.find((a) => a.agent_name === callee.name);
    const refusal = monitorCalleeRefusal(row, callee.name);
    if (refusal) issues.push({ path, message: refusal.message });
  }
  return issues;
}

export function monitorUnassignedToolMessage(toolName: string): string {
  return `this monitor has no tool "${toolName}" assigned to it. `
    + 'Create the monitor, assign the tool to it, then add the rule that calls it.';
}

// Relocated from the hub (`updateHubBody`) to the monitor agent — restore the
// guards the hub UI enforced (`delay_seconds >= 10`) on every write path so a caller
// (REST/CLI/MCP) can't persist an invalid monitor schedule. JSON object on the monitor
// agent column; `.passthrough()` keeps other agent keys flowing. Meaningful only on
// `monitor` agents.
//
// `trigger` joins this field here because this is the PR that ships its reader:
// `signalMonitor` arms only an `idle` monitor, so setting any other trigger stops
// that monitor being scheduled. The block above states why a key with no reader
// stays off this field.
//
// `delay_seconds` is no longer unconditional. It describes the `idle` fire and
// nothing else reads it, so requiring it of a `user_message`, `assistant_reply`
// or `manual` monitor would be asking an operator for a number with no meaning.
// It stays REQUIRED for `idle` — including an absent `trigger`, which IS `idle` —
// so no monitor that schedules can be saved without the delay it schedules on.
const monitorConfigField = z
  .object({
    delay_seconds: z.number().int().min(MONITOR_DELAY_SECONDS_MIN).optional(),
    trigger: monitorTriggerSchema.optional(),
    ...MONITOR_INPUT_SHAPING_SCHEMAS,
    ...MONITOR_RULE_SCHEMAS,
    ...MONITOR_CALL_SCHEMAS,
    flag_conditions: z.array(flagConditionSchema).optional(),
  })
  .passthrough()
  .superRefine((config, ctx) => {
    if (monitorConfigNeedsDelay(config)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['delay_seconds'],
        message: MONITOR_IDLE_DELAY_REQUIRED_MESSAGE,
      });
    }
    for (const key of monitorInputShapingKeysOnIdle(config)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: MONITOR_INPUT_SHAPING_IDLE_MESSAGE,
      });
    }
    for (const key of monitorCallKeysOffTrigger(config)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: MONITOR_CALL_KEY_MESSAGE });
    }
    // The decision keys. The same three refusals the CI refine applies, off the
    // same function, so an operator meeting one on `wayai push` and then in
    // Settings is told the same thing. Whether the monitor CARRIES the tool is
    // decided by the route, which can see the hub.
    for (const issue of collectMonitorRuleIssues(config)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    }
  })
  .nullable()
  .optional();

/**
 * The evaluator agent's top-level `flag_conditions`. Both bodies are
 * `.passthrough()` and the route forwards the validated body to HubDO verbatim, so
 * without this key any operator string reached the agent column — the one write path
 * of the four that a hand-built `PATCH /api/setup/agents/:id` (typed client, CLI,
 * `way_`-token curl) can drive. `null` clears the column; an absent key leaves it.
 */
const flagConditionsField = z.array(flagConditionSchema).nullable().optional();

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

export const agentIdParam = z.object({
  id: uuidSchema,
});

export const listAgentsQuery = z.object({
  hub_id: uuidSchema,
});

export const agentConnectionsQuery = z.object({
  hub_id: uuidSchema,
});

/**
 * Refuse a create body that binds a decisions model to an agent whose output a
 * decisions model cannot produce.
 *
 * This judges the BODY, which on create is the whole agent: role, settings,
 * `response_format` and `schema_json` all arrive together, so a body naming a
 * decisions model carries everything the check needs. A body naming no model, or
 * a model the predicate does not match, is not this refine's business and passes.
 *
 * A PATCH carries an arbitrary subset, so the same question cannot be answered
 * from the body alone: the agent route resolves the effective binding against the
 * stored row before the DO write, and `createAgent`/`updateAgent` in
 * `apps/backend/src/durable-objects/hub-do/hub-agent-ops.ts` run the same check
 * for the writers that reach the column without passing a route.
 */
function refineDecisionsModelBinding(body: unknown, ctx: z.RefinementCtx): void {
  const agent = body as Record<string, unknown>;
  const refusal = checkDecisionsModelBinding({
    model: readAgentSettingsModel(agent.agent_settings),
    agent_role: agent.agent_role,
    response_format: agent.response_format,
    schema_json: agent.schema_json,
  });
  if (refusal) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['agent_settings', 'model'], message: refusal.message });
  }
}

/**
 * Refuse a create body that is a monitor with rules on text output. A create body is
 * the whole agent, so it carries the role, the format and the config together; a PATCH
 * carries a subset and is judged by the agent route against the stored row.
 */
function refineMonitorRulesStructuredOutput(body: unknown, ctx: z.RefinementCtx): void {
  const refusal = monitorRulesStructuredOutputRefusal(body as MonitorRulesOutputFields);
  if (refusal) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['response_format'], message: refusal });
}

export const createAgentBody = z.object({
  agent_name: z.string().min(1),
  hub_id: uuidSchema,
  agent_role: z.enum(AGENT_ROLES),
  additional_context_template: z.string().max(20000).optional(),
  summarization_threshold_tokens: summarizationThresholdField,
  previous_conversations_count: previousConversationsCountField,
  monitor_config: monitorConfigField,
  flag_conditions: flagConditionsField,
}).passthrough().superRefine((body, ctx) => {
  refineDecisionsModelBinding(body, ctx);
  refineMonitorRulesStructuredOutput(body, ctx);
});

export const updateAgentBody = z.object({
  additional_context_template: z.string().max(20000).optional(),
  summarization_threshold_tokens: summarizationThresholdField,
  previous_conversations_count: previousConversationsCountField,
  monitor_config: monitorConfigField,
  flag_conditions: flagConditionsField,
}).passthrough();

// -- Agent parameters (a.k.a. evaluation variables) ---------------------------

export const AGENT_PARAMETER_TYPES = ['string', 'number', 'boolean', 'integer', 'enum'] as const;

// Mirror of the backend `VALID_VARIABLE_NAME_REGEX`
// (apps/backend/src/clickhouse/types.ts) — kept in sync here (same pattern as
// slug.ts) because the contract can't import from apps/backend. The name becomes
// a ClickHouse analytics path segment (data.variables.<name> /
// data.eval_scores.<name>), so a name the analytics layer would silently drop
// must be rejected at the write edge, not persisted.
export const AGENT_PARAMETER_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/;

/**
 * One agent parameter on create. `.strict()` rejects unknown keys — the
 * `agent_parameter` table has a fixed column set, so there's nothing to pass
 * through (unlike the agent row, which uses `.passthrough()`). Validates the
 * per-field shape plus the `parameter_name`/`parameter_type` guards the CI write
 * path enforces (`normalizeYamlEvalVars` in routes/api/ci/agent-parameters.ts), so
 * the REST path can't persist a malformed/analytics-breaking parameter. The CI
 * path's cross-field invariants (enum requires values, is_summary only for
 * strings, no duplicate names in a batch) are intentionally out of scope here —
 * the read path (`normalizeDbEvalVars`) already degrades those gracefully. (gh #2870)
 */
export const agentParameterCreateSchema = z
  .object({
    parameter_name: z
      .string()
      .regex(
        AGENT_PARAMETER_NAME_REGEX,
        'parameter_name must start with a letter or underscore and contain only letters, digits, and underscores (max 64 chars)',
      ),
    parameter_type: z.enum(AGENT_PARAMETER_TYPES).optional(),
    // No length caps on the free-text fields below: the agent_parameter columns are
    // unbounded TEXT and pre-PR rows were never length-validated, so a cap would 422
    // an unrelated GET→edit→re-save of legitimately-long existing data. The name
    // regex (above) and the batch cap (below) are the load-bearing guards; total
    // payload size belongs in a global /api/* body limit, not per field here.
    parameter_description: z.string().nullable().optional(),
    parameter_label: z.string().nullable().optional(),
    parameter_enum: z.array(z.string()).nullable().optional(),
    parameter_required: z.boolean().optional(),
    // Free-form analytics grouping label — no DB CHECK and the CI path persists
    // arbitrary strings, so keep this permissive (not the 4-value UI dropdown enum).
    variable_category: z.string().nullable().optional(),
    is_summary: z.boolean().optional(),
  })
  .strict();

// Batch cap: the body is forwarded element-by-element into the single-threaded
// HubDO write loop (one INSERT + one SELECT each), and /api/* carries no bodyLimit
// middleware — an unbounded array would let one hub:write caller drive an O(n)
// write storm. A hub agent has at most a few dozen parameters in practice.
export const MAX_AGENT_PARAMETERS_PER_REQUEST = 100;

/**
 * POST body for `POST /api/setup/agents/:id/parameters`. Accepts a single
 * parameter or a batch — the frontend always sends an array; `HubDO.createAgentParameter`
 * accepts both, so preserve that dual contract.
 */
export const createAgentParametersBody = z.union([
  agentParameterCreateSchema,
  z
    .array(agentParameterCreateSchema)
    .max(MAX_AGENT_PARAMETERS_PER_REQUEST, `too many parameters (max ${MAX_AGENT_PARAMETERS_PER_REQUEST})`),
]);

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** Full agent row as returned by select('*') on the agent table */
export interface AgentResponse {
  agent_id: string;
  agent_name: string;
  created_at: string;
  agent_sid: string | null;
  instructions: string;
  response_format: string;
  updated_at: string | null;
  hub_id: string;
  connection_id: string;
  agent_role: AgentRole;
  schema_json: Record<string, unknown> | null;
  schema_name: string | null;
  agent_settings: Record<string, unknown> | null;
  instructions_file_id: string | null;
  additional_context_template: string;
  enabled: boolean;
  include_message_timestamps: boolean;
  auto_format: boolean;
  /** Conversation-flagging conditions, evaluated post-close by the
   *  `conversation_evaluator` agent (relocated from the hub column). Only
   *  meaningful on `conversation_evaluator` agents; null/omitted elsewhere. */
  flag_conditions: FlagCondition[] | null;
  /** Token threshold that fires the rolling summarizer (sum of input + cache_read +
   *  cache_creation). A `summarizer` agent setting (relocated from the hub column).
   *  Only meaningful on `summarizer` agents; null/omitted elsewhere → falls back to
   *  DEFAULT_SUMMARIZATION_THRESHOLD_TOKENS at the read site. */
  summarization_threshold_tokens?: number | null;
  /** How many of the user's most recent ended conversations this agent receives as
   *  context, injected into the conversation's first user message and frozen there.
   *  Null/0 = off. Ignored on background roles. Bounded by
   *  `PREVIOUS_CONVERSATIONS_MAX`. */
  previous_conversations_count?: number | null;
  /** Monitor scheduling + flagging config. A `monitor` agent setting (relocated
   *  from the hub column). Only meaningful on `monitor` agents; null/omitted
   *  elsewhere. The monitor is NOT auto-provisioned.
   *
   *  `delay_seconds` is optional because it describes the `idle` fire alone; the
   *  write contract requires it exactly when the effective trigger is `idle`.
   *  An absent `trigger` reads as `idle` — `resolveMonitorTrigger`. */
  monitor_config?: MonitorConfig | null;
  /** A `pilot_voice` agent's own call openings, by language (`call-openings.ts`), as stored:
   *  read them through `readStoredCallOpenings`. Written only by HubDO's
   *  `setAgentCallOpening` (the `PUT/DELETE /setup/agents/:id/call-openings/:language` routes,
   *  and `wayai push`), and carried by the branching writes: to production by publish and
   *  sync, and into a new preview by replicate. No other agent write sets it. Null/omitted when every language plays the platform's opening. */
  call_openings?: StoredCallOpenings | null;
}

/** Agent parameter row */
export interface AgentParameterResponse {
  agent_parameter_id: string;
  created_at: string;
  agent_id: string;
  parameter_name: string;
  parameter_type: 'string' | 'number' | 'boolean' | 'integer' | 'enum';
  parameter_description: string;
  parameter_label: string | null;
  parameter_enum: string[] | null;
  parameter_required: boolean;
  variable_category: 'quality' | 'satisfaction' | 'compliance' | 'categorization' | null;
  is_summary: boolean;
}

/** Simplified agent connection detail returned by fetchAgentById */
export interface AgentConnectionDetail {
  connection_id: string;
  connection_display_name: string | null;
  connector_id: string;
  agent_settings_schema: Record<string, unknown> | null;
}

// -- Endpoint response wrappers --

export interface ListAgentsResponse {
  data: AgentResponse[];
}

export interface GetAgentResponse {
  data: {
    agent: AgentResponse;
    agent_connections: AgentConnectionDetail[];
    agent_parameters: AgentParameterResponse[];
  };
}

export interface CreateAgentResponse {
  data: AgentResponse[];
}

export interface UpdateAgentResponse {
  data: AgentResponse[];
}

export interface CreateAgentParametersResponse {
  data: AgentParameterResponse[];
}
